import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { apiClient } from '@/lib/api.ts'
import { cn, formatDate } from '@/lib/utils.ts'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import {
  ArrowLeft, Play, Square, Clock, MessageSquare, Send, Lock,
  User, Building2, Calendar, Tag, ChevronDown, Plus, AlertCircle,
  Copy, Check, RefreshCw, Shield, Phone, FileText,
  Mail, X, Layers, Euro, Timer, StickyNote,
  ShoppingCart, Loader2, Pencil, Monitor, ToggleLeft, ToggleRight, Bot,
  MessageCircle,
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import { useSapItemSearch } from '@/hooks/useSapItemSearch'
import { Button, Checkbox, EmptyState, ErrorState, IconButton, Input, LoadingState, Modal, Select, StatusBadge, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, useToast } from '@/components/ui'
import { type Quote } from '@/api/quotes.ts'
import {
  ActivityDetailModal,
  TicketActivityFeed,
  buildActivitiesFromTanssHistory,
  type ActivityItem,
  type TanssHistoryRaw,
} from '@/components/tickets'
import { TicketAIAgent } from './TicketAIAgent'
import { QuotationViewModal } from './QuotationViewPage'
import { NeuesAngebotModal } from '@/pages/auftraege/NeuesAngebotModal'
import { AngebotDialogPage } from '@/pages/auftraege/AngebotDialogPage'

// ─── Password Generator (inline) ───────────────────────────────────────────

const CHARSET = {
  upper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lower: 'abcdefghijklmnopqrstuvwxyz',
  numbers: '0123456789',
  special: '!@#$%^&*()-_=+[]{}|;:,.<>?',
}

function generatePassword(length: number): string {
  const charset = CHARSET.upper + CHARSET.lower + CHARSET.numbers + CHARSET.special
  const arr = new Uint8Array(length)
  crypto.getRandomValues(arr)
  return Array.from(arr).map((b) => charset[b % charset.length]).join('')
}

// ─── Helper: linkify phone numbers ──────────────────────────────────────────

function linkifyPhones(text: string): React.ReactNode[] {
  const phoneRegex = /(\+?\d[\d\s\-/()]{6,}\d)/g
  const parts = text.split(phoneRegex)
  return parts.map((part, i) => {
    if (phoneRegex.test(part)) {
      const cleanNum = part.replace(/[\s\-/()]/g, '')
      return (
        <a key={i} href={`tel:${cleanNum}`} className="text-brand-600 hover:underline inline-flex items-center gap-1">
          <Phone className="w-3 h-3" />
          {part}
        </a>
      )
    }
    return <span key={i}>{part}</span>
  })
}

function formatTotalMinutes(mins: number): string {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${h}h ${String(m).padStart(2, '0')}min`
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function TicketDetailPage() {
  const a = useAnonymize()
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()

  const { data: ticket, isLoading, isError: ticketIsError, error: ticketError } = useQuery({
    queryKey: ['tickets', id],
    queryFn: () => apiClient.get(`/tickets/${id}`).then((r) => r.data),
    enabled: !!id,
  })

  // Fetch metadata from TANSS
  const { data: tanssStates = [] } = useQuery({
    queryKey: ['tanss', 'states'],
    queryFn: () => apiClient.get('/tickets/meta/states').then((r) => r.data),
    staleTime: 60_000,
  })

  const { data: tanssTypes = [] } = useQuery({
    queryKey: ['tanss', 'types'],
    queryFn: () => apiClient.get('/tickets/meta/types').then((r) => r.data),
    staleTime: 60_000,
  })

  const { data: tanssEmployees = [] } = useQuery({
    queryKey: ['tanss', 'employees'],
    queryFn: () => apiClient.get('/tickets/meta/tanss-employees').then((r) => r.data),
    staleTime: 60_000,
  })

  const { data: tanssDepartments = [] } = useQuery({
    queryKey: ['tanss', 'departments'],
    queryFn: () => apiClient.get('/tickets/meta/departments').then((r) => r.data),
    staleTime: 60_000,
  })

  // Company search state
  const [companySearch, setCompanySearch] = useState('')
  const [showCompanySearch, setShowCompanySearch] = useState(false)
  const companySearchRef = useRef<HTMLDivElement>(null)

  const { data: searchedCompanies = [] } = useQuery<Array<{ id: number; name: string; postCode?: string; city?: string; displayId?: string }>>({
    queryKey: ['tanss', 'companies', companySearch],
    queryFn: () => apiClient
      .get<{ data: Array<{ id: number; name: string; postCode?: string; city?: string; displayId?: string }> }>(
        '/tanss/companies/search',
        { params: { q: companySearch } },
      )
      .then((r) => r.data.data),
    enabled: companySearch.length >= 2,
    staleTime: 30_000,
  })

  // Fetch company persons (for Auftraggeber dropdown)
  const companyId = ticket?.companyId
  const { data: companyPersons = [] } = useQuery<Array<{ id: number; name?: string; firstName?: string; lastName?: string }>>({
    queryKey: ['tanss', 'company-persons', companyId],
    queryFn: () => apiClient
      .get<{ data: Array<{ id: number; name?: string; firstName?: string; lastName?: string }> }>(
        `/tanss/companies/${companyId}/persons`,
      )
      .then((r) => r.data.data),
    enabled: !!companyId,
    staleTime: 60_000,
  })

  // Fetch company devices (for Zuweisung dropdown)
  const { data: companyDevices = [] } = useQuery({
    queryKey: ['tanss', 'company-devices', companyId],
    queryFn: () => apiClient.get(`/tickets/meta/company-devices/${companyId}`).then((r) => r.data),
    enabled: !!companyId,
    staleTime: 60_000,
  })

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.patch(`/tickets/${id}/update`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets', id] }),
  })

  const { data: linkedDocs } = useQuery<LinkedDocsResponse>({
    queryKey: ['tickets', id, 'linked-documents'],
    queryFn: () => apiClient.get(`/tickets/${id}/linked-documents`).then((r) => r.data),
    enabled: !!id,
    retry: 1,
    staleTime: 5 * 60_000,       // 5 Minuten cached
    refetchInterval: 5 * 60_000, // Alle 5 Minuten im Hintergrund aktualisieren
  })

  const { data: kiQuotesForBadge = [] } = useQuery<Quote[]>({
    queryKey: ['tickets', id, 'ki-angebote'],
    queryFn: () => apiClient.get(`/tickets/${id}/ki-angebote`).then((r) => r.data.data as Quote[]),
    enabled: !!id,
    staleTime: 60_000,
  })

  const [activeTab, setActiveTab] = useState<'activity' | 'quotations' | 'orders' | 'password' | 'password-send' | 'emails' | 'email-compose' | 'whatsapp'>('activity')

  // Inline editing state for title, description, internalNote, estimatedMinutes, serviceCap
  const [editingField, setEditingField] = useState<'title' | 'description' | 'internalNote' | 'estimatedMinutes' | 'serviceCap' | null>(null)
  const [editValue, setEditValue] = useState('')
  const editRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  const startEditing = useCallback((field: 'title' | 'description' | 'internalNote' | 'estimatedMinutes' | 'serviceCap', currentValue: string) => {
    setEditingField(field)
    setEditValue(currentValue)
  }, [])

  const cancelEditing = useCallback(() => {
    setEditingField(null)
    setEditValue('')
  }, [])

  const saveEditing = useCallback(() => {
    if (!editingField || !ticket) return

    if (editingField === 'estimatedMinutes' || editingField === 'serviceCap') {
      const val = editValue.trim()

      // Service-Obergrenze: detect euro amount — requires € symbol (e.g. "500€", "1200.50 €")
      if (editingField === 'serviceCap' && /^[\d.,]+\s*€$/.test(val)) {
        const amount = parseFloat(val.replace(/[€\s]/g, '').replace(',', '.'))
        if (!isNaN(amount)) {
          const original = ticket.tanssDetails?.serviceCapAmount ?? 0
          if (amount !== original) {
            updateMutation.mutate({ serviceCapAmount: amount || null, serviceCapMinutes: null })
          }
        }
        setEditingField(null)
        setEditValue('')
        return
      }

      // Parse flexible time formats: "1h", "2h 30m", "30m", "2:00", "2" (= 2h)
      let totalMinutes = 0
      if (/^\d+$/.test(val)) {
        // Plain number = hours
        totalMinutes = parseInt(val, 10) * 60
      } else if (val.includes(':')) {
        // h:mm format
        const parts = val.split(':')
        totalMinutes = (parseInt(parts[0] || '0', 10) || 0) * 60 + (parseInt(parts[1] || '0', 10) || 0)
      } else {
        // "1h", "2h 30m", "30m", "1h30m" etc.
        const hMatch = val.match(/(\d+)\s*h/i)
        const mMatch = val.match(/(\d+)\s*m(?:in)?/i)
        if (hMatch) totalMinutes += parseInt(hMatch[1], 10) * 60
        if (mMatch) totalMinutes += parseInt(mMatch[1], 10)
        // Fallback: if nothing matched, try parsing as decimal hours (e.g. "3.5" = 3h 30m)
        if (!hMatch && !mMatch) {
          const num = parseFloat(val.replace(',', '.'))
          if (!isNaN(num)) totalMinutes = Math.round(num * 60)
        }
      }
      const field = editingField === 'estimatedMinutes' ? 'estimatedMinutes' : 'serviceCapMinutes'
      const original = editingField === 'estimatedMinutes'
        ? (ticket.tanssDetails?.estimatedMinutes ?? 0)
        : (ticket.tanssDetails?.serviceCapMinutes ?? 0)
      if (totalMinutes !== original) {
        const mutation: Record<string, unknown> = { [field]: totalMinutes || null }
        if (editingField === 'serviceCap') mutation.serviceCapAmount = null
        updateMutation.mutate(mutation)
      }
      setEditingField(null)
      setEditValue('')
      return
    }

    const fieldMap = { title: 'title', description: 'description', internalNote: 'internalNote' } as const
    const originalValue = editingField === 'title' ? (ticket.subject ?? '') :
      editingField === 'description' ? (ticket.description ?? '') :
      (ticket.tanssDetails?.internalNote ?? '')
    if (editValue !== originalValue) {
      updateMutation.mutate({ [fieldMap[editingField]]: editValue })
    }
    setEditingField(null)
    setEditValue('')
  }, [editingField, editValue, ticket, updateMutation])

  // Focus the input/textarea when editing starts
  useEffect(() => {
    if (editingField && editRef.current) {
      editRef.current.focus()
    }
  }, [editingField])

  // Click-outside for company search
  useEffect(() => {
    if (!showCompanySearch) return
    const handler = (e: MouseEvent) => {
      if (companySearchRef.current && !companySearchRef.current.contains(e.target as Node)) {
        setShowCompanySearch(false)
        setCompanySearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showCompanySearch])

  // Zuweisung options: devices + company employees (like TANSS "Zuweisung waehlen" dialog)
  // NOTE: Must be before early returns to satisfy React's rules of hooks
  const zuweisungOptions = useMemo(() => {
    const options: { id: number; name: string; group: string; linkTypeId?: number }[] = []
    if (Array.isArray(companyDevices)) {
      companyDevices.forEach((d: { id: number; name?: string; title?: string; type?: string; category?: string; linkTypeId?: number }) => {
        const name = d.name || d.title || `Gerät #${d.id}`
        const group = d.category || d.type || 'Geräte'
        options.push({ id: d.id, name, group, linkTypeId: d.linkTypeId })
      })
    }
    if (Array.isArray(companyPersons)) {
      companyPersons.forEach((p: { id: number; name?: string; firstName?: string; lastName?: string }) => {
        const name = p.name || [p.firstName, p.lastName].filter(Boolean).join(' ') || `Mitarbeiter #${p.id}`
        options.push({ id: p.id, name, group: 'Mitarbeiter', linkTypeId: 5 })
      })
    }
    return options
  }, [companyDevices, companyPersons])

  if (isLoading) {
    return (
      <div className="space-y-4 pb-20">
        <BackButton />
        <div className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse space-y-3">
          <div className="h-6 bg-gray-100 rounded w-1/2" />
          <div className="h-4 bg-gray-100 rounded w-full" />
          <div className="h-4 bg-gray-100 rounded w-3/4" />
        </div>
      </div>
    )
  }

  if (ticketIsError) {
    return (
      <div className="space-y-4">
        <BackButton />
        <ErrorState
          title="Ticket konnte nicht geladen werden"
          description={(ticketError as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Bitte erneut versuchen.'}
          details={ticketError ? JSON.stringify(ticketError, Object.getOwnPropertyNames(ticketError), 2) : undefined}
          onRetry={() => window.location.reload()}
        />
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className="space-y-4">
        <BackButton />
        <ErrorState
          title="Ticket nicht gefunden"
          description="Dieses Ticket existiert nicht oder wurde gelöscht."
        />
      </div>
    )
  }

  const currentState = tanssStates.find((s: { id: number }) => s.id === ticket.statusId)
  const currentType = tanssTypes.find((t: { id: number }) => t.id === ticket.typeId)
  const td = (ticket.tanssDetails ?? {}) as Record<string, unknown>

  // Resolve Auftraggeber name: TANSS response may not include the name, so look up from persons list
  const resolvedRemitterName = (() => {
    if (td.remitterName) return String(td.remitterName)
    if (td.orderByName) return String(td.orderByName)
    if (td.remitterId && Array.isArray(companyPersons)) {
      const p = companyPersons.find((p: { id: number }) => p.id === td.remitterId) as { name?: string; firstName?: string; lastName?: string } | undefined
      if (p) return p.name || [p.firstName, p.lastName].filter(Boolean).join(' ') || ''
    }
    return ''
  })()

  // Helper values for the right info panel
  const sepBilling = Boolean(td.separateBilling ?? td.independentBilling)
  const estimatedMins = td.estimatedMinutes != null ? Number(td.estimatedMinutes) : 0
  let serviceCap = '-'
  if (td.serviceCapAmount != null && Number(td.serviceCapAmount) > 0) {
    serviceCap = `${Number(td.serviceCapAmount).toFixed(2)} €`
  } else if (td.serviceCapMinutes != null && Number(td.serviceCapMinutes) > 0) {
    serviceCap = formatTotalMinutes(Number(td.serviceCapMinutes))
  }
  if (serviceCap !== '-' && td.serviceCapExceeded) serviceCap += ' (überschritten)'

  return (
    <div className="space-y-4 pb-24">
      <BackButton />

      {/* ── Zeile 1: Ticket-Ueberschrift (editierbar Doppelklick) ── */}
      <div className="min-w-0">
        {editingField === 'title' ? (
          <div className="flex items-center gap-2 w-full">
            <input
              ref={editRef as React.RefObject<HTMLInputElement>}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEditing()
                if (e.key === 'Escape') cancelEditing()
              }}
              className="text-xl font-bold text-gray-900 bg-white border border-brand-400 rounded px-2 py-0.5 w-full outline-none ring-2 ring-brand-200"
            />
            <button onClick={saveEditing} className="shrink-0 px-2 py-1 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded transition-colors">
              <Check className="w-4 h-4" />
            </button>
            <button onClick={cancelEditing} className="shrink-0 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <h1
            className="text-xl font-bold text-gray-900 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 transition-colors group flex items-center gap-1.5"
            onDoubleClick={() => startEditing('title', ticket.subject ?? '')}
            title="Doppelklick zum Bearbeiten"
          >
            {ticket.subject}
            <Pencil className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
          </h1>
        )}
      </div>

      {/* ── Zeile 2: Ticketnummer | Firma | Auftraggeber | Zuweisung ── */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {/* Ticketnummer */}
        <a
          href={`https://tanssdev.implec.de/index.php?section=bug&sub=view&bugID=${ticket.tanssId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-brand-600 font-mono hover:underline shrink-0"
        >
          #{ticket.tanssId}
        </a>
        <span className="text-gray-300">|</span>

        {/* Firma (editierbar Doppelklick → Suche) */}
        <div className="relative" ref={companySearchRef}>
          {showCompanySearch ? (
            <div className="flex flex-col">
              <input
                type="text"
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                placeholder="Firma suchen..."
                className="text-sm border border-brand-400 rounded px-2 py-0.5 outline-none ring-2 ring-brand-200 min-w-[200px]"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { setShowCompanySearch(false); setCompanySearch('') }
                }}
              />
              {companySearch.length >= 2 && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[280px] max-h-[240px] overflow-y-auto">
                  {searchedCompanies.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400">Keine Firmen gefunden</div>
                  ) : (
                    searchedCompanies.map((c: { id: number; name: string; postCode?: string; city?: string; displayId?: string }) => (
                      <button
                        key={c.id}
                        onClick={() => {
                          updateMutation.mutate({ companyId: c.id })
                          setShowCompanySearch(false)
                          setCompanySearch('')
                        }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span>
                          <span className="font-medium">{c.name}</span>
                          {(c.postCode || c.city) && (
                            <span className="text-xs text-gray-400 ml-1">{[c.postCode, c.city].filter(Boolean).join(' ')}</span>
                          )}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          ) : (
            <span
              className="flex items-center gap-1 text-gray-700 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 transition-colors group"
              onDoubleClick={() => setShowCompanySearch(true)}
              title="Doppelklick zum Ändern"
            >
              <Building2 className="w-3.5 h-3.5 text-gray-400" />
              {ticket.customer?.name ? a.name(ticket.customer.name) : <span className="text-gray-400 italic">Keine Firma</span>}
              <Pencil className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
          )}
        </div>
        <span className="text-gray-300">|</span>

        {/* Auftraggeber (editierbar Dropdown – Mitarbeiter der Firma) */}
        <AuftraggeberDropdown
          currentName={resolvedRemitterName}
          currentId={td.remitterId as number | null}
          persons={companyPersons}
          onChange={(remitterId) => updateMutation.mutate({ remitterId })}
        />
        <span className="text-gray-300">|</span>

        {/* Zuweisung (editierbar Dropdown – Geraete: Server, PC, Peripherie, etc.) */}
        <ZuweisungDropdown
          currentName={td.linkName as string || ''}
          currentId={td.linkId as number | null}
          currentLinkTypeId={td.linkTypeId as number | null}
          options={zuweisungOptions}
          onChange={(linkId, linkTypeId) => updateMutation.mutate({ linkId, linkTypeId })}
        />
      </div>

      {/* ── Zeile 3: Status, Typ, Mitarbeiter, Abteilung, Faelligkeit, Deadline (farbige abgerundete Badges) ── */}
      <div className="flex flex-wrap items-center gap-2">
        <TanssDropdown
          label="Status"
          currentId={ticket.statusId}
          currentName={currentState?.name ?? ticket.status}
          options={tanssStates}
          onChange={(statusId) => updateMutation.mutate({ statusId })}
          color="bg-blue-100 text-blue-700"
        />
        <TanssDropdown
          label="Typ"
          currentId={ticket.typeId ?? 0}
          currentName={currentType?.name ?? 'Unklassifiziert'}
          options={tanssTypes.length > 0 ? tanssTypes : [{ id: 0, name: 'Unklassifiziert' }]}
          onChange={(typeId) => updateMutation.mutate({ typeId })}
          color="bg-purple-100 text-purple-700"
        />
        <TanssDropdown
          label="Mitarbeiter"
          currentId={ticket.assignedToEmployeeId}
          currentName={
            ticket.assignedToEmployeeName
            || tanssEmployees.find((e: { id: number }) => e.id === ticket.assignedToEmployeeId)?.name
            || 'Nicht zugewiesen'
          }
          options={tanssEmployees}
          onChange={(assignedToEmployeeId) => updateMutation.mutate({ assignedToEmployeeId })}
          color="bg-green-100 text-green-700"
          icon={<User className="w-3 h-3" />}
          nullable
        />
        <TanssDropdown
          label="Abteilung"
          currentId={ticket.assignedToDepartmentId}
          currentName={
            ticket.assignedToDepartmentName
            || tanssDepartments.find((d: { id: number }) => d.id === ticket.assignedToDepartmentId)?.name
            || 'Keine Abteilung'
          }
          options={tanssDepartments}
          onChange={(assignedToDepartmentId) => updateMutation.mutate({ assignedToDepartmentId })}
          color="bg-orange-100 text-orange-700"
          icon={<Layers className="w-3 h-3" />}
          nullable
        />
        <DueDatePicker
          label="Fälligkeit"
          dueDate={ticket.dueDate as string | null}
          onChange={(dueDate) => updateMutation.mutate({ dueDate })}
        />
        <DueDatePicker
          label="Deadline"
          dueDate={ticket.deadline as string | null}
          onChange={(deadline) => updateMutation.mutate({ deadline })}
        />
      </div>

      {/* ── Zeile 4: Tags (loeschbar, neue hinzufuegbar) ── */}
      <TicketTags ticketId={id!} tags={ticket.tags ?? []} />

      {/* ── Zeile 5: Beschreibung (links 2/3) + rechtes Info-Panel (1/3) ── */}
      <div className="flex gap-4 items-stretch">
        {/* Links: Ticket-Beschreibung */}
        <div className="w-2/3 min-w-0">
          <div
            className={cn(
              "bg-white rounded-lg border border-gray-200 p-4 transition-colors h-full",
              editingField !== 'description' && "cursor-pointer hover:border-gray-300 group"
            )}
            onDoubleClick={() => editingField !== 'description' && startEditing('description', ticket.description ?? '')}
            title={editingField !== 'description' ? "Doppelklick zum Bearbeiten" : undefined}
          >
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2">
              <FileText className="w-3.5 h-3.5" />
              Beschreibung
              {editingField !== 'description' && (
                <Pencil className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </div>
            {editingField === 'description' ? (
              <div className="space-y-2">
                <textarea
                  ref={editRef as React.RefObject<HTMLTextAreaElement>}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') cancelEditing()
                  }}
                  rows={6}
                  className="text-sm text-gray-700 w-full bg-white border border-brand-400 rounded px-2 py-1 outline-none ring-2 ring-brand-200 resize-y leading-relaxed"
                />
                <div className="flex items-center gap-2">
                  <button onClick={saveEditing} className="px-3 py-1 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded transition-colors flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" /> OK
                  </button>
                  <button onClick={cancelEditing} className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors flex items-center gap-1">
                    <X className="w-3.5 h-3.5" /> Abbrechen
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {ticket.description ? linkifyPhones(ticket.description) : <span className="text-gray-400 italic">Keine Beschreibung</span>}
              </div>
            )}
          </div>
        </div>

        {/* Rechts: Info-Panel mit Abrechnung, Aufwand, Service-Obergrenze, interne Bemerkung */}
        <div className="w-1/3 min-w-0 flex flex-col gap-3">
          {/* Eigenstaendige Abrechnung Toggle */}
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                <Euro className="w-3.5 h-3.5" />
                Eigenständige Abrechnung
              </span>
              <button
                onClick={() => updateMutation.mutate({ separateBilling: !sepBilling })}
                className="text-gray-500 hover:text-brand-600 transition-colors"
                title={sepBilling ? 'Deaktivieren' : 'Aktivieren'}
              >
                {sepBilling ? (
                  <ToggleRight className="w-6 h-6 text-brand-600" />
                ) : (
                  <ToggleLeft className="w-6 h-6 text-gray-400" />
                )}
              </button>
            </div>
            {sepBilling && (
              <div className="mt-2 text-xs text-gray-500 italic">
                Abrechnung erfolgt eigenständig
              </div>
            )}
          </div>

          {/* Geschaetzter Aufwand */}
          <div
            className={cn(
              "bg-white rounded-lg border border-gray-200 p-3",
              editingField !== 'estimatedMinutes' && "cursor-pointer hover:border-gray-300 group"
            )}
            onDoubleClick={() => editingField !== 'estimatedMinutes' && startEditing('estimatedMinutes', estimatedMins > 0 ? `${Math.floor(estimatedMins / 60)}:${String(estimatedMins % 60).padStart(2, '0')}` : '0:00')}
            title={editingField !== 'estimatedMinutes' ? "Doppelklick zum Bearbeiten" : undefined}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                <Timer className="w-3.5 h-3.5" />
                Geschätzter Aufwand
                {editingField !== 'estimatedMinutes' && (
                  <Pencil className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </span>
              {editingField === 'estimatedMinutes' ? (
                <div className="flex items-center gap-1.5">
                  <input
                    ref={editRef as React.RefObject<HTMLInputElement>}
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEditing(); if (e.key === 'Escape') cancelEditing() }}
                    placeholder="2h 30m"
                    className="w-20 text-sm text-right border border-gray-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <button onClick={saveEditing} className="text-green-600 hover:text-green-700"><Check className="w-4 h-4" /></button>
                  <button onClick={cancelEditing} className="text-red-500 hover:text-red-600"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <span className="text-sm font-medium text-gray-800">
                  {estimatedMins > 0 ? `${Math.floor(estimatedMins / 60)}:${String(estimatedMins % 60).padStart(2, '0')}` : '-'}
                </span>
              )}
            </div>
          </div>

          {/* Service-Obergrenze */}
          <div
            className={cn(
              "bg-white rounded-lg border border-gray-200 p-3",
              editingField !== 'serviceCap' && "cursor-pointer hover:border-gray-300 group"
            )}
            onDoubleClick={() => {
              if (editingField === 'serviceCap') return
              let currentVal = ''
              if (td.serviceCapAmount != null && Number(td.serviceCapAmount) > 0) {
                currentVal = String(Number(td.serviceCapAmount))
              } else if (td.serviceCapMinutes != null && Number(td.serviceCapMinutes) > 0) {
                const capMins = Number(td.serviceCapMinutes)
                currentVal = `${Math.floor(capMins / 60)}:${String(capMins % 60).padStart(2, '0')}`
              }
              startEditing('serviceCap', currentVal)
            }}
            title={editingField !== 'serviceCap' ? "Doppelklick zum Bearbeiten" : undefined}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" />
                Service-Obergrenze
                {editingField !== 'serviceCap' && (
                  <Pencil className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </span>
              {editingField === 'serviceCap' ? (
                <div className="flex items-center gap-1.5">
                  <input
                    ref={editRef as React.RefObject<HTMLInputElement>}
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEditing(); if (e.key === 'Escape') cancelEditing() }}
                    placeholder="2h / 500€"
                    className="w-20 text-sm text-right border border-gray-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <button onClick={saveEditing} className="text-green-600 hover:text-green-700"><Check className="w-4 h-4" /></button>
                  <button onClick={cancelEditing} className="text-red-500 hover:text-red-600"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <span className={cn('text-sm font-medium', serviceCap === '-' ? 'text-gray-400' : td.serviceCapExceeded ? 'text-red-600' : 'text-gray-800')}>
                  {serviceCap}
                </span>
              )}
            </div>
          </div>

          {/* Interne Bemerkung */}
          <div
            className={cn(
              "bg-amber-50 rounded-lg border border-amber-200 p-3 transition-colors flex-1",
              editingField !== 'internalNote' && "cursor-pointer hover:border-amber-300 group"
            )}
            onDoubleClick={() => editingField !== 'internalNote' && startEditing('internalNote', ticket.tanssDetails?.internalNote ?? '')}
            title={editingField !== 'internalNote' ? "Doppelklick zum Bearbeiten" : undefined}
          >
            <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 mb-1">
              <Lock className="w-3.5 h-3.5" />
              Interne Bemerkung
              {editingField !== 'internalNote' && (
                <Pencil className="w-3 h-3 text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </div>
            {editingField === 'internalNote' ? (
              <div className="space-y-2">
                <textarea
                  ref={editRef as React.RefObject<HTMLTextAreaElement>}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') cancelEditing()
                  }}
                  rows={4}
                  className="text-sm text-amber-900 w-full bg-amber-50 border border-amber-400 rounded px-2 py-1 outline-none ring-2 ring-amber-200 resize-y leading-relaxed"
                />
                <div className="flex items-center gap-2">
                  <button onClick={saveEditing} className="px-3 py-1 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 rounded transition-colors flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" /> OK
                  </button>
                  <button onClick={cancelEditing} className="px-3 py-1 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded transition-colors flex items-center gap-1">
                    <X className="w-3.5 h-3.5" /> Abbrechen
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">
                {ticket.tanssDetails?.internalNote || <span className="text-amber-400 italic">Keine interne Bemerkung</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="activity">
            <MessageSquare className="w-3.5 h-3.5" />
            Kommentare, E-Mails, Leistungen
          </TabsTrigger>
          <TabsTrigger value="quotations">
            <ShoppingCart className="w-3.5 h-3.5" />
            {(() => {
              const sapCount = linkedDocs?.quotations?.length ?? 0
              const kiCount = kiQuotesForBadge.length
              const total = sapCount + kiCount
              return `Angebote${total > 0 ? ` (${total})` : ''}`
            })()}
          </TabsTrigger>
          <TabsTrigger value="orders">
            <FileText className="w-3.5 h-3.5" />
            Aufträge{linkedDocs?.orders?.length ? ` (${linkedDocs.orders.length})` : ''}
          </TabsTrigger>
          <TabsTrigger value="emails">
            <Mail className="w-3.5 h-3.5" />
            E-Mails
          </TabsTrigger>
          <TabsTrigger value="email-compose">
            <Send className="w-3.5 h-3.5" />
            E-Mail schreiben
          </TabsTrigger>
          <TabsTrigger value="whatsapp">
            <MessageCircle className="w-3.5 h-3.5" />
            WhatsApp
          </TabsTrigger>
          <TabsTrigger value="password">
            <Shield className="w-3.5 h-3.5" />
            Passwort
          </TabsTrigger>
          <TabsTrigger value="password-send">
            <Send className="w-3.5 h-3.5" />
            PW versenden
          </TabsTrigger>
        </TabsList>
        <TabsContent value="activity" className="pt-0">
          <div className="bg-white rounded-lg border border-gray-200">
            <ActivityFeed ticketId={id!} ticket={ticket} />
          </div>
        </TabsContent>
        <TabsContent value="quotations" className="pt-0">
          <div className="bg-white rounded-lg border border-gray-200">
            <LinkedDocumentsTab ticketId={id!} ticket={ticket} docType="quotations" />
          </div>
        </TabsContent>
        <TabsContent value="orders" className="pt-0">
          <div className="bg-white rounded-lg border border-gray-200">
            <LinkedDocumentsTab ticketId={id!} ticket={ticket} docType="orders" />
          </div>
        </TabsContent>
        <TabsContent value="emails" className="pt-0">
          <div className="bg-white rounded-lg border border-gray-200">
            <TicketEmailsTab ticketId={id!} />
          </div>
        </TabsContent>
        <TabsContent value="email-compose" className="pt-0">
          <div className="bg-white rounded-lg border border-gray-200">
            <TicketEmailComposeTab ticketId={id!} ticket={ticket} onSent={() => setActiveTab('emails')} />
          </div>
        </TabsContent>
        <TabsContent value="whatsapp" className="pt-0">
          <div className="bg-white rounded-lg border border-gray-200">
            <TicketWhatsAppTab ticketId={id!} ticket={ticket} />
          </div>
        </TabsContent>
        <TabsContent value="password" className="pt-0">
          <div className="bg-white rounded-lg border border-gray-200">
            <PasswordGeneratorTab />
          </div>
        </TabsContent>
        <TabsContent value="password-send" className="pt-0">
          <div className="bg-white rounded-lg border border-gray-200">
            <PasswordSendTab />
          </div>
        </TabsContent>
      </Tabs>

      {/* Sticky Timer at Bottom */}
      <StickyTimer ticketId={id!} />

      {/* Floating AI Agent */}
      <TicketAIAgent ticketId={id!} />
    </div>
  )
}

// ─── Linked SAP Documents Tab ───────────────────────────────────────────────

type LineType = 'article' | 'text' | 'subtotal'

interface SapDocumentLine {
  lineNum: number
  lineType: LineType
  itemCode: string
  description: string
  freeText: string
  quantity: number
  price: number
  purchasePrice: number
  margin: number
  discount: number
  lineTotal: number
  parentLine: number | null
  isBomParent: boolean
}

interface SapDocument {
  sapDocEntry: number
  sapDocNum: number
  date: string
  customerCode: string
  customerName: string
  grossTotal: number
  netTotal: number
  currency: string
  status: string
  notes?: string
  ticketId?: string | null
  lines: SapDocumentLine[]
}

interface LinkedDocsResponse {
  quotations: SapDocument[]
  orders: SapDocument[]
  parsed?: { angebote: number[]; auftraege: number[] }
  message?: string
}

const KI_SCENARIO_LABELS: Record<string, string> = {
  laptop: 'Client-Hardware',
  teams: 'Teams-Migration',
  meetingRoom: 'Besprechungsraum',
  free: 'Freier Dialog',
}

function LinkedDocumentsTab({ ticketId, ticket, docType }: { ticketId: string; ticket: Record<string, unknown>; docType: 'quotations' | 'orders' }) {
  const qc = useQueryClient()
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingDoc, setEditingDoc] = useState<SapDocument | null>(null)
  const [duplicatingDoc, setDuplicatingDoc] = useState<SapDocument | null>(null)
  const [viewingDoc, setViewingDoc] = useState<SapDocument | null>(null)
  const [expandedDoc] = useState<string | null>(null)

  // KI-Angebote state
  const [showKiWizard, setShowKiWizard] = useState(false)
  const [openKiQuoteId, setOpenKiQuoteId] = useState<string | null>(null)

  const { data, isLoading, error, refetch, isFetching } = useQuery<LinkedDocsResponse>({
    queryKey: ['tickets', ticketId, 'linked-documents'],
    queryFn: () => apiClient.get(`/tickets/${ticketId}/linked-documents`).then((r) => r.data),
    retry: 1,
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  })

  // KI quotes linked to this ticket
  const { data: kiQuotes = [], refetch: refetchKiQuotes } = useQuery<Quote[]>({
    queryKey: ['tickets', ticketId, 'ki-angebote'],
    queryFn: () => apiClient.get(`/tickets/${ticketId}/ki-angebote`).then((r) => r.data.data as Quote[]),
    enabled: docType === 'quotations',
    staleTime: 60_000,
  })

  const activeDocs = docType === 'quotations' ? (data?.quotations ?? []) : (data?.orders ?? [])
  const type = docType === 'quotations' ? 'quotation' as const : 'order' as const

  // Extract customer info from ticket for KI wizard pre-fill
  const ticketCustomerName =
    ((ticket?.customer as { name?: string } | null)?.name) ??
    (ticket?.companyName as string | undefined) ??
    undefined
  const ticketCompanyTanssId =
    ticket?.companyId != null ? String(ticket.companyId) : undefined

  return (
    <div>
      {/* Header with create buttons */}
      {docType === 'quotations' && (
        <div className="flex items-center justify-end gap-2 border-b border-gray-100 px-4 py-2">
          <Button variant="secondary" size="sm" onClick={() => setShowKiWizard(true)}>
            <Bot className="w-3.5 h-3.5" />
            KI-Angebot erstellen
          </Button>
          <Button variant="primary" size="sm" onClick={() => setShowCreateForm(true)}>
            <Plus className="w-3.5 h-3.5" />
            Angebot erstellen
          </Button>
        </div>
      )}

      <div className="p-4 space-y-6">
        {/* KI-Angebote section */}
        {docType === 'quotations' && kiQuotes.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              KI-Angebote ({kiQuotes.length})
            </p>
            <div className="space-y-2">
              {kiQuotes.map((q) => (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => setOpenKiQuoteId(q.id)}
                  className="w-full text-left rounded-lg border border-gray-200 bg-white p-3 hover:border-brand-300 hover:bg-brand-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Bot className="w-4 h-4 text-brand-500 shrink-0" />
                      <span className="text-small font-semibold text-gray-800 truncate">{q.title}</span>
                    </div>
                    <StatusBadge status={`quote_${q.status}`} />
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-caption text-gray-500">
                    {q.customerName && <span>{q.customerName}</span>}
                    {q.customerName && <span>·</span>}
                    <span>{KI_SCENARIO_LABELS[q.scenario] ?? q.scenario}</span>
                    <span>·</span>
                    <span>{formatDate(q.createdAt)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* SAP quotations / orders section */}
        <div>
          {docType === 'quotations' && kiQuotes.length > 0 && (
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              SAP-Angebote
            </p>
          )}
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 text-gray-400 py-8">
              <Loader2 className="w-4 h-4 animate-spin" />
              Lade verknüpfte Dokumente...
            </div>
          ) : error ? (
            <ErrorState
              size="sm"
              title="Fehler beim Laden der SAP-Dokumente"
              details={(error as Error)?.message ?? undefined}
              onRetry={() => refetch()}
            />
          ) : data?.message ? (
            <div className="text-center text-sm py-8">
              <AlertCircle className="w-5 h-5 mx-auto mb-2 text-amber-500" />
              <p className="text-amber-600 mb-3">{data.message}</p>
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={cn('w-3 h-3', isFetching && 'animate-spin')} />
                Erneut versuchen
              </button>
            </div>
          ) : activeDocs.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-8">
              Keine {docType === 'quotations' ? 'SAP-Angebote' : 'Aufträge'} verknüpft
            </div>
          ) : (
            <div className="space-y-2">
              {activeDocs.map((doc) => (
                <SapDocumentCard
                  key={`${type[0]}-${doc.sapDocEntry}`}
                  doc={doc}
                  type={type}
                  expanded={expandedDoc === `${type[0]}-${doc.sapDocEntry}`}
                  onToggle={() => setViewingDoc(doc)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* KI-Angebot Wizard */}
      {showKiWizard && (
        <NeuesAngebotModal
          open={showKiWizard}
          onOpenChange={setShowKiWizard}
          onManualMode={() => {
            setShowKiWizard(false)
            setShowCreateForm(true)
          }}
          ticketId={ticketId}
          initialCustomerName={ticketCustomerName}
          initialCustomerTanssId={ticketCompanyTanssId}
          onQuoteCreated={(quoteId) => {
            setShowKiWizard(false)
            refetchKiQuotes()
            setOpenKiQuoteId(quoteId)
          }}
        />
      )}

      {/* KI-Angebot Detail Modal (embedded AngebotDialogPage) */}
      {openKiQuoteId && (
        <Modal
          open={!!openKiQuoteId}
          onOpenChange={(v) => { if (!v) setOpenKiQuoteId(null) }}
          title={
            <div className="flex items-center gap-2 w-full">
              <Bot className="w-4 h-4 text-brand-500 shrink-0" />
              <span>KI-Angebot</span>
              {/* Portal target for action buttons from AngebotDialogPage (embedded mode) */}
              <div id="quote-header-actions-slot" className="ml-auto flex items-center gap-2 flex-shrink-0" />
            </div>
          }
          size="full"
          contentClassName="p-0 overflow-hidden flex flex-col min-h-0"
        >
          <AngebotDialogPage
            quoteId={openKiQuoteId}
            embedded
            onClose={() => setOpenKiQuoteId(null)}
            onDeleted={() => {
              setOpenKiQuoteId(null)
              refetchKiQuotes()
            }}
          />
        </Modal>
      )}

      {/* Create Quotation Dialog */}
      {showCreateForm && (
        <CreateQuotationDialog
          ticketId={ticketId}
          ticket={ticket}
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false)
            qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'linked-documents'] })
            qc.invalidateQueries({ queryKey: ['tickets', ticketId] })
          }}
        />
      )}

      {/* Document Detail Modal */}
      {viewingDoc && type === 'quotation' && (
        <QuotationViewModal
          ticketId={ticketId}
          doc={viewingDoc}
          onClose={() => setViewingDoc(null)}
        />
      )}
      {viewingDoc && type === 'order' && (
        <DocumentDetailModal
          doc={viewingDoc}
          type={type}
          onClose={() => setViewingDoc(null)}
        />
      )}

      {/* Duplicate Quotation Dialog */}
      {duplicatingDoc && (
        <CreateQuotationDialog
          ticketId={ticketId}
          ticket={ticket}
          prefill={duplicatingDoc}
          onClose={() => setDuplicatingDoc(null)}
          onSuccess={() => {
            setDuplicatingDoc(null)
            qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'linked-documents'] })
            qc.invalidateQueries({ queryKey: ['tickets', ticketId] })
          }}
        />
      )}

      {/* Edit Quotation Dialog */}
      {editingDoc && (
        <EditQuotationDialog
          ticketId={ticketId}
          doc={editingDoc}
          onClose={() => setEditingDoc(null)}
          onSuccess={() => {
            setEditingDoc(null)
            qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'linked-documents'] })
          }}
        />
      )}
    </div>
  )
}

function SapDocumentCard({ doc, type, onToggle }: {
  doc: SapDocument
  type: 'quotation' | 'order'
  expanded?: boolean
  onToggle: () => void
  onEdit?: () => void
  onDuplicate?: () => void
}) {
  const a = useAnonymize()
  const isOpen = doc.status === 'open'
  const typeLabel = type === 'quotation' ? 'AN' : 'AU'
  const typeBg = type === 'quotation' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
      >
        <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded', typeBg)}>
          {typeLabel}
        </span>
        <span className="text-sm font-medium text-gray-900 font-mono">#{a.docNum(doc.sapDocNum)}</span>
        <span className="text-xs text-gray-500 truncate flex-1">{a.name(doc.customerName)}</span>
        <span className="text-xs text-gray-400">{formatDate(doc.date)}</span>
        <span className={cn(
          'text-[10px] font-medium px-1.5 py-0.5 rounded',
          isOpen ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500',
        )}>
          {isOpen ? 'Offen' : 'Geschlossen'}
        </span>
        <span className="text-sm font-semibold text-gray-900 tabular-nums">{a.fmtCurrency(doc.grossTotal)}</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
    </div>
  )
}

// ─── Document Detail Modal ──────────────────────────────────────────────────

function DocumentDetailModal({ doc, type, onClose, onEdit, onDuplicate }: {
  doc: SapDocument
  type: 'quotation' | 'order'
  onClose: () => void
  onEdit?: () => void
  onDuplicate?: () => void
}) {
  const a = useAnonymize()
  const isOpen = doc.status === 'open'
  const typeLabel = type === 'quotation' ? 'Angebot' : 'Auftrag'

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <span className={cn(
            'text-xs font-bold px-2 py-1 rounded',
            type === 'quotation' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700',
          )}>
            {type === 'quotation' ? 'AN' : 'AU'}
          </span>
          <span>{typeLabel} #{a.docNum(doc.sapDocNum)}</span>
          <span className={cn(
            'text-xs font-medium px-2 py-0.5 rounded',
            isOpen ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500',
          )}>
            {isOpen ? 'Offen' : 'Geschlossen'}
          </span>
        </div>
      }
      size="lg"
      footer={
        <>
          {onDuplicate && (
            <Button variant="secondary" size="sm" onClick={onDuplicate}>
              <Copy className="w-3.5 h-3.5" />
              Als neues Angebot kopieren
            </Button>
          )}
          {onEdit && (
            <Button variant="primary" size="sm" onClick={onEdit}>
              <Pencil className="w-3.5 h-3.5" />
              Bearbeiten
            </Button>
          )}
          {!onEdit && !onDuplicate && (
            <Button variant="secondary" size="sm" onClick={onClose}>Schließen</Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {/* Info */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-xs text-gray-400 block">Kunde</span>
            <span className="font-medium text-gray-900">{a.name(doc.customerName)}</span>
            <span className="text-xs text-gray-400 font-mono ml-1">{a.code(doc.customerCode)}</span>
          </div>
          <div>
            <span className="text-xs text-gray-400 block">Datum</span>
            <span className="font-medium text-gray-900">{formatDate(doc.date)}</span>
          </div>
          <div>
            <span className="text-xs text-gray-400 block">Netto</span>
            <span className="font-medium text-gray-900">{a.fmtCurrency(doc.netTotal)}</span>
          </div>
          <div>
            <span className="text-xs text-gray-400 block">Brutto</span>
            <span className="font-semibold text-gray-900">{a.fmtCurrency(doc.grossTotal)}</span>
          </div>
        </div>

        {doc.notes && (
          <div>
            <span className="text-xs text-gray-400 block mb-1">Bemerkung</span>
            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">{a.freeText(doc.notes)}</p>
          </div>
        )}

        {/* Positionen */}
        {doc.lines.length > 0 && (
          <div>
            <span className="text-xs text-gray-400 block mb-2">Positionen</span>
            <div className="space-y-0.5">
              {doc.lines.map((line) => {
                if (line.lineType === 'text') {
                  return (
                    <div key={line.lineNum} className="flex items-center gap-2 px-2 py-1 bg-blue-50/50 rounded text-sm">
                      <span className="text-[10px] font-bold text-blue-500">TXT</span>
                      <span className="text-gray-700">{a.freeText(line.description)}</span>
                    </div>
                  )
                }
                if (line.lineType === 'subtotal') {
                  return (
                    <div key={line.lineNum} className="flex items-center justify-between px-2 py-1 bg-amber-50/50 rounded text-sm font-medium border-t border-amber-200">
                      <span className="text-[10px] font-bold text-amber-600">SUM</span>
                      <span className="text-gray-700">{a.freeText(line.description)}</span>
                      <span className="text-gray-900 tabular-nums">{a.fmtCurrency(line.lineTotal)}</span>
                    </div>
                  )
                }
                return (
                  <div key={line.lineNum} className={cn(
                    'grid grid-cols-[1fr_60px_80px_80px_90px] gap-2 items-center px-2 py-1 text-sm border-b border-gray-100',
                    line.parentLine != null && 'ml-4 text-gray-500',
                  )}>
                    <div>
                      <span className="font-mono text-[10px] text-gray-400">{a.code(line.itemCode)}</span>
                      <span className="ml-1.5 text-gray-700">{a.freeText(line.description)}</span>
                      {line.freeText && <p className="text-xs text-gray-400 mt-0.5">{a.freeText(line.freeText)}</p>}
                    </div>
                    <span className="text-right text-gray-600">{line.quantity}</span>
                    <span className="text-right text-[10px] text-gray-400 tabular-nums">
                      {line.purchasePrice > 0 ? `E${a.fmtCurrency(line.purchasePrice)} M${line.margin.toFixed(0)}%` : '—'}
                    </span>
                    <span className="text-right text-gray-600">{a.fmtCurrency(line.price)}</span>
                    <span className="text-right font-medium text-gray-900 tabular-nums">{a.fmtCurrency(line.lineTotal)}</span>
                  </div>
                )
              })}
            </div>
            <div className="flex justify-end gap-4 border-t border-gray-200 pt-2 mt-2 text-sm">
              <span className="text-gray-500">Netto: {a.fmtCurrency(doc.netTotal)}</span>
              <span className="font-semibold text-gray-900">Brutto: {a.fmtCurrency(doc.grossTotal)}</span>
            </div>
          </div>
        )}

        {!isOpen && type === 'quotation' && (
          <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Dieses Angebot ist geschlossen und kann nicht mehr bearbeitet werden. Sie können eine Kopie als neues Angebot erstellen.
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── Create Quotation Dialog ────────────────────────────────────────────────

interface QuotationLine {
  lineType: LineType
  itemCode: string
  description: string
  freeText: string           // Zeilenerweiterung
  quantity: number
  unitPrice: number
  purchasePrice: number      // EK
  margin: number             // Marge %
  parentLine: number | null  // uebergeordnete Zeile (Stueckliste)
}

function emptyArticleLine(): QuotationLine {
  return { lineType: 'article', itemCode: '', description: '', freeText: '', quantity: 1, unitPrice: 0, purchasePrice: 0, margin: 0, parentLine: null }
}
function emptyTextLine(): QuotationLine {
  return { lineType: 'text', itemCode: '', description: '', freeText: '', quantity: 0, unitPrice: 0, purchasePrice: 0, margin: 0, parentLine: null }
}
function emptySubtotalLine(): QuotationLine {
  return { lineType: 'subtotal', itemCode: '', description: 'Zwischensumme', freeText: '', quantity: 0, unitPrice: 0, purchasePrice: 0, margin: 0, parentLine: null }
}

// ─── Shared: Quotation Line Editor ─────────────────────────────────────────

function QuotationLineEditor({ lines, setLines }: {
  lines: QuotationLine[]
  setLines: React.Dispatch<React.SetStateAction<QuotationLine[]>>
}) {
  const [itemSearch, setItemSearch] = useState('')
  const [searchingLineIdx, setSearchingLineIdx] = useState<number | null>(null)
  const { data: items = [] } = useSapItemSearch(itemSearch)

  const addLine = (type: LineType) => {
    const newLine = type === 'text' ? emptyTextLine() : type === 'subtotal' ? emptySubtotalLine() : emptyArticleLine()
    setLines((l) => [...l, newLine])
  }
  const removeLine = (idx: number) => setLines((l) => l.filter((_, i) => i !== idx))
  const updateLine = (idx: number, updates: Partial<QuotationLine>) =>
    setLines((l) => l.map((line, i) => (i === idx ? { ...line, ...updates } : line)))
  const moveLine = (idx: number, dir: -1 | 1) => {
    setLines((l) => {
      const arr = [...l]
      const target = idx + dir
      if (target < 0 || target >= arr.length) return arr;
      [arr[idx], arr[target]] = [arr[target]!, arr[idx]!]
      return arr
    })
  }

  const total = lines.reduce((s, l) => l.lineType === 'article' ? s + l.quantity * l.unitPrice : s, 0)

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-gray-600">Positionen</label>
        <div className="flex gap-2">
          <button onClick={() => addLine('article')} className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Artikel
          </button>
          <button onClick={() => addLine('text')} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Text
          </button>
          <button onClick={() => addLine('subtotal')} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Summe
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="grid grid-cols-[auto_1fr_70px_80px_80px_80px_auto] gap-1 text-[10px] text-gray-400 font-medium px-1 mb-1">
        <span className="w-6"></span>
        <span>Artikel / Beschreibung</span>
        <span className="text-center">Menge</span>
        <span className="text-right">EK+Marge</span>
        <span className="text-right">Stückpreis</span>
        <span className="text-right">Gesamt</span>
        <span className="w-6"></span>
      </div>

      <div className="space-y-1">
        {lines.map((line, idx) => (
          <div key={idx} className={cn(
            'rounded-lg border px-2 py-1.5',
            line.lineType === 'text' ? 'border-blue-200 bg-blue-50/50' :
            line.lineType === 'subtotal' ? 'border-amber-200 bg-amber-50/50' :
            line.parentLine != null ? 'border-gray-200 bg-gray-50/50 ml-6' :
            'border-gray-200',
          )}>
            {line.lineType === 'text' ? (
              /* ─── Textzeile ─── */
              <div className="flex gap-2 items-center">
                <span className="text-[10px] font-bold text-blue-500 w-6 text-center">TXT</span>
                <Input
                  type="text"
                  value={line.description}
                  onChange={(e) => updateLine(idx, { description: e.target.value })}
                  placeholder="Freitext eingeben..."
                  className="border-0 bg-transparent text-sm focus:ring-0 shadow-none"
                  wrapperClassName="flex-1"
                />
                <IconButton onClick={() => removeLine(idx)} icon={<X className="w-3.5 h-3.5" />} aria-label="Zeile entfernen" variant="ghost" size="xs" className="text-gray-400 hover:text-red-500" />
              </div>
            ) : line.lineType === 'subtotal' ? (
              /* ─── Summenzeile ─── */
              <div className="flex gap-2 items-center">
                <span className="text-[10px] font-bold text-amber-600 w-6 text-center">SUM</span>
                <Input
                  type="text"
                  value={line.description}
                  onChange={(e) => updateLine(idx, { description: e.target.value })}
                  className="border-0 bg-transparent text-sm font-medium focus:ring-0 shadow-none"
                  wrapperClassName="flex-1"
                />
                <span className="text-sm font-semibold text-gray-900 tabular-nums w-20 text-right">
                  {formatCurrency(lines.slice(0, idx).reduce((s, l) => {
                    // Sum all articles since last subtotal
                    if (l.lineType === 'subtotal') return 0
                    if (l.lineType === 'article') return s + l.quantity * l.unitPrice
                    return s
                  }, 0))}
                </span>
                <IconButton onClick={() => removeLine(idx)} icon={<X className="w-3.5 h-3.5" />} aria-label="Zeile entfernen" variant="ghost" size="xs" className="text-gray-400 hover:text-red-500" />
              </div>
            ) : (
              /* ─── Artikelzeile ─── */
              <div className="space-y-1">
                <div className="grid grid-cols-[auto_1fr_70px_80px_80px_80px_auto] gap-1 items-center">
                  {/* Type badge + move */}
                  <div className="flex flex-col items-center w-6 gap-0.5">
                    <button onClick={() => moveLine(idx, -1)} className="text-gray-300 hover:text-gray-500 leading-none text-[10px]">▲</button>
                    <button onClick={() => moveLine(idx, 1)} className="text-gray-300 hover:text-gray-500 leading-none text-[10px]">▼</button>
                  </div>

                  {/* Artikel-Suche */}
                  <div className="relative">
                    <Input
                      type="text"
                      value={line.description || line.itemCode}
                      onChange={(e) => {
                        updateLine(idx, { itemCode: '', description: e.target.value })
                        setItemSearch(e.target.value)
                        setSearchingLineIdx(idx)
                      }}
                      placeholder="Artikel suchen..."
                      className="text-sm"
                    />
                    {searchingLineIdx === idx && itemSearch.length >= 2 && !line.itemCode && items.length > 0 && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {items.map((item) => (
                          <button
                            key={item.ItemCode}
                            onClick={() => {
                              updateLine(idx, {
                                itemCode: item.ItemCode,
                                description: item.ItemName,
                                unitPrice: item.SalesPrice || 0,
                                purchasePrice: item.PurchasePrice || 0,
                                margin: item.SalesPrice && item.PurchasePrice
                                  ? Number((((item.SalesPrice - item.PurchasePrice) / item.SalesPrice) * 100).toFixed(1))
                                  : 0,
                              })
                              setSearchingLineIdx(null)
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            <span className="font-mono text-[10px] text-gray-400 shrink-0">{item.ItemCode}</span>
                            <span className="flex-1 truncate">{item.ItemName}</span>
                            <span className="text-xs text-gray-400 shrink-0">{formatCurrency(item.SalesPrice)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {line.itemCode && (
                      <span className="text-[10px] text-gray-400 font-mono">{line.itemCode}</span>
                    )}
                  </div>

                  {/* Menge */}
                  <Input
                    type="number"
                    min={1}
                    value={line.quantity}
                    onChange={(e) => updateLine(idx, { quantity: Math.max(1, Number(e.target.value)) })}
                    className="text-sm text-center"
                  />

                  {/* EK + Marge */}
                  <div className="text-right text-[10px] text-gray-500 tabular-nums leading-tight">
                    {line.purchasePrice > 0 ? (
                      <>
                        <span title="Einkaufspreis">E{formatCurrency(line.purchasePrice).replace(/\s/g, '')}</span>
                        <br />
                        <span title="Marge" className={line.margin < 0 ? 'text-red-500' : 'text-emerald-600'}>M{line.margin.toFixed(0)}%</span>
                      </>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </div>

                  {/* Stueckpreis */}
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={line.unitPrice}
                    onChange={(e) => {
                      const vk = Number(e.target.value)
                      const margin = vk > 0 && line.purchasePrice > 0
                        ? Number((((vk - line.purchasePrice) / vk) * 100).toFixed(1))
                        : 0
                      updateLine(idx, { unitPrice: vk, margin })
                    }}
                    className="text-sm text-right"
                  />

                  {/* Gesamt netto */}
                  <span className="text-sm font-medium text-gray-900 tabular-nums text-right">
                    {formatCurrency(line.quantity * line.unitPrice)}
                  </span>

                  {/* Remove */}
                  <IconButton onClick={() => removeLine(idx)} icon={<X className="w-3.5 h-3.5" />} aria-label="Zeile entfernen" variant="ghost" size="xs" className="text-gray-400 hover:text-red-500 w-6 justify-center" />
                </div>

                {/* Zeilenerweiterung */}
                {(line.freeText || line.itemCode) && (
                  <div className="ml-7">
                    <Input
                      type="text"
                      value={line.freeText}
                      onChange={(e) => updateLine(idx, { freeText: e.target.value })}
                      placeholder="Zeilenerweiterung (optionaler Zusatztext)..."
                      className="border-0 border-b border-dashed border-gray-200 bg-transparent text-xs text-gray-500 focus:ring-0 shadow-none rounded-none placeholder:text-gray-300"
                    />
                  </div>
                )}

                {/* Stueckliste: Unterartikel zuordnen */}
                {line.itemCode && (
                  <div className="ml-7 flex items-center gap-2">
                    <label className="text-[10px] text-gray-400">Übergeordnet:</label>
                    <select
                      value={line.parentLine ?? ''}
                      onChange={(e) => updateLine(idx, { parentLine: e.target.value ? Number(e.target.value) : null })}
                      className="border border-gray-200 rounded text-[10px] text-gray-500 px-1 py-0.5 bg-white"
                    >
                      <option value="">— Keine (eigenständig)</option>
                      {lines.map((other, otherIdx) => (
                        other.lineType === 'article' && other.itemCode && otherIdx !== idx && other.parentLine == null ? (
                          <option key={otherIdx} value={otherIdx}>
                            #{otherIdx + 1} {other.description || other.itemCode}
                          </option>
                        ) : null
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Gesamt */}
      <div className="flex justify-end text-sm font-semibold text-gray-900 border-t border-gray-100 pt-3 mt-3">
        Gesamt netto: {formatCurrency(total)}
      </div>
    </div>
  )
}

// ─── Create Quotation Dialog ────────────────────────────────────────────────

function CreateQuotationDialog({ ticketId, ticket, onClose, onSuccess, prefill }: {
  ticketId: string
  ticket: Record<string, unknown>
  onClose: () => void
  onSuccess: () => void
  prefill?: SapDocument | null
}) {
  const a = useAnonymize()
  const customer = ticket.customer as { name?: string; id?: number } | null
  const [cardCode, setCardCode] = useState(prefill?.customerCode ?? '')
  const [comments, setComments] = useState(prefill?.notes ?? '')
  const [lines, setLines] = useState<QuotationLine[]>(
    prefill?.lines?.length
      ? prefill.lines.map((l) => ({
          lineType: l.lineType,
          itemCode: l.itemCode,
          description: l.description,
          freeText: l.freeText ?? '',
          quantity: l.quantity,
          unitPrice: l.price,
          purchasePrice: l.purchasePrice ?? 0,
          margin: l.margin ?? 0,
          parentLine: l.parentLine,
        }))
      : [emptyArticleLine()],
  )
  const [partnerSearch, setPartnerSearch] = useState(prefill?.customerName ?? customer?.name ?? '')

  // Search SAP partners
  const { data: partners = [] } = useQuery<Array<{ CardCode: string; CardName: string }>>({
    queryKey: ['sap', 'partners', partnerSearch],
    queryFn: () => apiClient.get('/sap/partners', { params: { search: partnerSearch } }).then((r) => r.data),
    enabled: partnerSearch.length >= 2,
    staleTime: 10_000,
  })

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiClient.post(`/tickets/${ticketId}/create-quotation`, payload).then((r) => r.data),
    onSuccess,
  })

  const canSubmit = cardCode && lines.some((l) => l.lineType === 'article' && l.itemCode)

  const handleSubmit = () => {
    if (!canSubmit) return
    createMutation.mutate({
      cardCode,
      comments: comments || undefined,
      lines: lines.filter((l) => l.lineType !== 'article' || l.itemCode).map((l) => ({
        lineType: l.lineType,
        itemCode: l.itemCode || undefined,
        description: l.description || undefined,
        freeText: l.freeText || undefined,
        quantity: l.lineType === 'article' ? l.quantity : undefined,
        unitPrice: l.lineType === 'article' ? l.unitPrice : undefined,
        parentLine: l.parentLine,
      })),
    })
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={prefill ? `Kopie von Angebot #${a.docNum(prefill.sapDocNum)}` : 'Neues Angebot erstellen'}
      size="xl"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={createMutation.isPending}
          >
            Angebot erstellen
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Customer / CardCode */}
        <div>
          <div className="relative">
            <Input
              type="text"
              label="Kunde (SAP)"
              value={partnerSearch}
              onChange={(e) => { setPartnerSearch(e.target.value); setCardCode('') }}
              placeholder="Kundenname suchen..."
            />
            {partnerSearch.length >= 2 && !cardCode && partners.length > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {partners.map((p) => (
                  <button
                    key={p.CardCode}
                    onClick={() => { setCardCode(p.CardCode); setPartnerSearch(p.CardName) }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex justify-between"
                  >
                    <span>{a.name(p.CardName)}</span>
                    <span className="text-xs text-gray-400 font-mono">{a.code(p.CardCode)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {cardCode && <span className="text-xs text-gray-400 mt-1 block font-mono">{a.code(cardCode)}</span>}
        </div>

        {/* Bemerkung */}
        <Input
          type="text"
          label="Bemerkung"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="z.B. Angebot zu Ticket #..."
        />

        {/* Positionen (shared editor) */}
        <QuotationLineEditor lines={lines} setLines={setLines} />

        {/* Error */}
        {createMutation.error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            Fehler: {createMutation.error instanceof Error ? createMutation.error.message : 'Unbekannter Fehler'}
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── Edit Quotation Dialog ─────────────────────────────────────────────────

function EditQuotationDialog({ ticketId, doc, onClose, onSuccess }: {
  ticketId: string
  doc: SapDocument
  onClose: () => void
  onSuccess: () => void
}) {
  const a = useAnonymize()
  const [comments, setComments] = useState(doc.notes ?? '')
  const [lines, setLines] = useState<QuotationLine[]>(
    doc.lines.length > 0
      ? doc.lines.map((l) => ({
          lineType: l.lineType,
          itemCode: l.itemCode,
          description: l.description,
          freeText: l.freeText ?? '',
          quantity: l.quantity,
          unitPrice: l.price,
          purchasePrice: l.purchasePrice ?? 0,
          margin: l.margin ?? 0,
          parentLine: l.parentLine,
        }))
      : [emptyArticleLine()],
  )

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiClient.patch(`/tickets/${ticketId}/quotations/${doc.sapDocEntry}`, payload).then((r) => r.data),
    onSuccess,
  })

  const canSubmit = lines.some((l) => l.lineType === 'article' && l.itemCode)

  const handleSubmit = () => {
    if (!canSubmit) return
    updateMutation.mutate({
      comments: comments || undefined,
      lines: lines.filter((l) => l.lineType !== 'article' || l.itemCode).map((l) => ({
        lineType: l.lineType,
        itemCode: l.itemCode || undefined,
        description: l.description || undefined,
        freeText: l.freeText || undefined,
        quantity: l.lineType === 'article' ? l.quantity : undefined,
        unitPrice: l.lineType === 'article' ? l.unitPrice : undefined,
        parentLine: l.parentLine,
      })),
    })
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Angebot #${a.docNum(doc.sapDocNum)} bearbeiten`}
      size="xl"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={updateMutation.isPending}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Customer (readonly) */}
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Kunde</label>
          <div className="text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            {a.name(doc.customerName)} <span className="text-xs text-gray-400 font-mono ml-1">{a.code(doc.customerCode)}</span>
          </div>
        </div>

        {/* Bemerkung */}
        <Input
          type="text"
          label="Bemerkung"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
        />

        {/* Positionen (shared editor) */}
        <QuotationLineEditor lines={lines} setLines={setLines} />

        {/* Error */}
        {updateMutation.error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            Fehler: {updateMutation.error instanceof Error ? updateMutation.error.message : 'Unbekannter Fehler'}
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── Auftraggeber Dropdown ───────────────────────────────────────────────────

function AuftraggeberDropdown({
  currentName, currentId, persons, onChange,
}: {
  currentName: string
  currentId: number | null
  persons: { id: number; name?: string; firstName?: string; lastName?: string }[]
  onChange: (id: number | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setSearch('') }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const options = persons.map((p) => ({
    id: p.id,
    name: p.name || [p.firstName, p.lastName].filter(Boolean).join(' ') || `Person #${p.id}`,
  }))

  const filtered = options.filter((o) =>
    !search.trim() || o.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-sm text-gray-700 hover:bg-gray-50 rounded px-1 -mx-1 transition-colors group"
      >
        <User className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-gray-400 text-xs">Auftraggeber:</span>
        {currentName || <span className="text-gray-400 italic">Nicht gesetzt</span>}
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[240px] max-h-[300px] flex flex-col">
          <div className="px-2 py-1.5 border-b border-gray-100">
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Mitarbeiter suchen..."
              autoFocus
              className="text-sm"
            />
          </div>
          <div className="overflow-y-auto max-h-[240px]">
            <button
              onClick={() => { onChange(null); setOpen(false); setSearch('') }}
              className={cn('w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 text-gray-400 italic', currentId == null && 'font-semibold')}
            >
              Nicht gesetzt
            </button>
            {filtered.map((opt) => (
              <button
                key={opt.id}
                onClick={() => { onChange(opt.id); setOpen(false); setSearch('') }}
                className={cn('w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50', opt.id === currentId && 'font-semibold text-brand-700')}
              >
                {opt.name}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400">Keine Mitarbeiter gefunden</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Zuweisung Dropdown (Geraete: Server, PC, Peripherie, Komponenten, etc.) ──

function ZuweisungDropdown({
  currentName, currentId, currentLinkTypeId, options, onChange,
}: {
  currentName: string
  currentId: number | null
  currentLinkTypeId: number | null
  options: { id: number; name: string; group: string; linkTypeId?: number }[]
  onChange: (id: number | null, linkTypeId: number | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setSearch('') }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const filtered = options.filter((o) =>
    !search.trim() || o.name.toLowerCase().includes(search.toLowerCase())
  )

  // Group by group name
  const grouped = filtered.reduce<Record<string, typeof options>>((acc, opt) => {
    if (!acc[opt.group]) acc[opt.group] = []
    acc[opt.group].push(opt)
    return acc
  }, {})

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-sm text-gray-700 hover:bg-gray-50 rounded px-1 -mx-1 transition-colors group"
      >
        <Monitor className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-gray-400 text-xs">Zuweisung:</span>
        {currentName || <span className="text-gray-400 italic">Nicht zugewiesen</span>}
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[280px] max-h-[340px] flex flex-col">
          <div className="px-2 py-1.5 border-b border-gray-100">
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen..."
              autoFocus
              className="text-sm"
            />
          </div>
          <div className="overflow-y-auto max-h-[280px]">
            <button
              onClick={() => { onChange(null, null); setOpen(false); setSearch('') }}
              className={cn('w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 text-gray-400 italic', !currentId && 'font-semibold')}
            >
              Nicht zugewiesen
            </button>
            {Object.entries(grouped).map(([group, items]) => (
              <div key={group}>
                <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 pt-2 pb-0.5">
                  {group}
                </div>
                {items.map((opt) => {
                  const isSelected = opt.id === currentId && (opt.linkTypeId ?? null) === currentLinkTypeId
                  return (
                  <button
                    key={`${opt.linkTypeId}-${opt.id}`}
                    onClick={() => { onChange(opt.id, opt.linkTypeId ?? null); setOpen(false); setSearch('') }}
                    className={cn('w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 pl-5', isSelected && 'font-semibold text-brand-700')}
                  >
                    {opt.name}
                  </button>
                  )
                })}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400">Keine Einträge gefunden</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Back Button ────────────────────────────────────────────────────────────

function BackButton() {
  const navigate = useNavigate()
  return (
    <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
      <ArrowLeft className="w-4 h-4" />
      Zurück zur Liste
    </Button>
  )
}

// ─── Ticket Tags (add / remove) ─────────────────────────────────────────────

interface TanssTag {
  id: number
  name: string
  backgroundColor?: string
  fontColor?: string
  groupTagId?: number
  isGroupParent?: boolean
  groupTag?: { id: number; name: string; backgroundColor?: string; fontColor?: string }
}

function hexToStyle(bg?: string, fg?: string): React.CSSProperties | undefined {
  if (!bg && !fg) return undefined
  const normBg = bg?.startsWith('#') ? bg : bg ? `#${bg}` : undefined
  const normFg = fg?.startsWith('#') ? fg : fg ? `#${fg}` : undefined
  return {
    ...(normBg ? { backgroundColor: normBg } : {}),
    ...(normFg ? { color: normFg } : {}),
  }
}

function TicketTags({ ticketId, tags }: { ticketId: string; tags: (TanssTag | string)[] }) {
  const qc = useQueryClient()
  const [showPicker, setShowPicker] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  const { data: allTags = [] } = useQuery<TanssTag[]>({
    queryKey: ['tanss', 'tags'],
    queryFn: () => apiClient.get('/tickets/meta/tags').then((r) => {
      const data = r.data
      return Array.isArray(data) ? data : data?.content ?? data?.data ?? []
    }),
    staleTime: 60_000,
  })

  const addTag = useMutation({
    mutationFn: (tagId: number) => apiClient.post(`/tickets/${ticketId}/tags`, { tagId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets', ticketId] })
      setShowPicker(false)
      setSearch('')
    },
  })

  const removeTag = useMutation({
    mutationFn: (tagId: number) => apiClient.delete(`/tickets/${ticketId}/tags/${tagId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets', ticketId] }),
  })

  useEffect(() => {
    if (!showPicker) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowPicker(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showPicker])

  // Normalize tags
  const currentTags: TanssTag[] = tags.map((t) =>
    typeof t === 'string' ? { id: 0, name: t } : t,
  )

  const currentTagIds = new Set(currentTags.map((t) => t.id))
  const availableTags = allTags
    .filter((t) => !currentTagIds.has(t.id))
    .filter((t) => !search.trim() || t.name.toLowerCase().includes(search.toLowerCase()) ||
      (t.groupTag?.name && t.groupTag.name.toLowerCase().includes(search.toLowerCase())))

  // Sort available tags by group name, then tag name
  const sortedAvailableTags = [...availableTags].sort((a, b) => {
    const groupA = a.groupTag?.name ?? ''
    const groupB = b.groupTag?.name ?? ''
    if (groupA !== groupB) return groupA.localeCompare(groupB)
    return a.name.localeCompare(b.name)
  })

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Tag className="w-4 h-4 text-gray-500" />
      {currentTags.map((tag, i) => (
        <span
          key={tag.id || i}
          className="inline-flex items-center gap-0 rounded-full text-xs font-medium group overflow-hidden border border-black/10 shadow-sm"
        >
          {/* Group/parent tag prefix */}
          {tag.groupTag && (
            <span
              className="px-2 py-1 font-semibold border-r border-black/10"
              style={hexToStyle(tag.groupTag.backgroundColor, tag.groupTag.fontColor) ?? { backgroundColor: '#e5e7eb', color: '#374151' }}
            >
              {tag.groupTag.name}
            </span>
          )}
          {/* Tag itself */}
          <span
            className="px-2 py-1"
            style={hexToStyle(tag.backgroundColor, tag.fontColor) ?? { backgroundColor: '#f3f4f6', color: '#4b5563' }}
          >
            {tag.name}
          </span>
          {tag.id !== 0 && (
            <button
              onClick={() => removeTag.mutate(tag.id)}
              className="px-1.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 hover:text-red-600"
              style={hexToStyle(tag.backgroundColor, tag.fontColor) ?? { backgroundColor: '#f3f4f6' }}
              title="Tag entfernen"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}

      {/* Add tag button + picker */}
      <div className="relative" ref={ref}>
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="inline-flex items-center gap-0.5 px-2.5 py-1 rounded-full border border-dashed border-gray-300 text-gray-400 text-xs hover:border-gray-400 hover:text-gray-600 transition-colors"
        >
          <Plus className="w-3 h-3" />
          Tag
        </button>
        {showPicker && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[250px] max-h-[300px] flex flex-col">
            <div className="px-2 py-1.5 border-b border-gray-100">
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tag suchen..."
                autoFocus
                className="text-sm"
              />
            </div>
            <div className="overflow-y-auto max-h-[240px]">
              {sortedAvailableTags.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400">Keine Tags verfügbar</div>
              ) : (
                (() => {
                  let lastGroup: string | undefined
                  return sortedAvailableTags.map((tag) => {
                    const groupName = tag.groupTag?.name
                    const showHeader = groupName !== lastGroup
                    lastGroup = groupName
                    return (
                      <div key={tag.id}>
                        {showHeader && groupName && (
                          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 pt-2 pb-0.5">
                            {groupName}
                          </div>
                        )}
                        <button
                          onClick={() => addTag.mutate(tag.id)}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                          <span className="inline-flex items-center rounded-full overflow-hidden border border-black/10 text-xs">
                            {tag.groupTag && (
                              <span
                                className="px-1.5 py-0.5 font-semibold border-r border-black/10"
                                style={hexToStyle(tag.groupTag.backgroundColor, tag.groupTag.fontColor)}
                              >
                                {tag.groupTag.name}
                              </span>
                            )}
                            <span
                              className="px-1.5 py-0.5"
                              style={hexToStyle(tag.backgroundColor, tag.fontColor)}
                            >
                              {tag.name}
                            </span>
                          </span>
                        </button>
                      </div>
                    )
                  })
                })()
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Due Date Picker ────────────────────────────────────────────────────────

function getDueDateColor(dueDate: string | null): { bg: string; text: string; label: string } {
  if (!dueDate) return { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Keine Fälligkeit' }
  const now = Date.now()
  const due = new Date(dueDate).getTime()
  const fourHours = 4 * 60 * 60 * 1000
  if (due < now) return { bg: 'bg-red-100', text: 'text-red-700', label: 'Überfällig' }
  if (due - now < fourHours) return { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Bald fällig' }
  return { bg: 'bg-green-100', text: 'text-green-700', label: 'Fällig' }
}

function DueDatePicker({ dueDate, onChange, label = 'Fällig' }: { dueDate: string | null; onChange: (d: string | null) => void; label?: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const color = getDueDateColor(dueDate)

  const current = dueDate ? new Date(dueDate) : new Date()
  const [viewYear, setViewYear] = useState(current.getFullYear())
  const [viewMonth, setViewMonth] = useState(current.getMonth())
  const [selectedHour, setSelectedHour] = useState(dueDate ? current.getHours() : 8)
  const [selectedMinute, setSelectedMinute] = useState(dueDate ? current.getMinutes() : 0)
  // Track local pending date (not yet sent to server)
  const [pendingDate, setPendingDate] = useState<string | null>(null)

  // Reset state when dropdown opens
  useEffect(() => {
    if (open && dueDate) {
      const d = new Date(dueDate)
      setViewYear(d.getFullYear())
      setViewMonth(d.getMonth())
      setSelectedHour(d.getHours())
      setSelectedMinute(d.getMinutes())
      setPendingDate(null)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const pad = (n: number) => String(n).padStart(2, '0')

  // Selected day (date-only for comparison) — prefer pending over saved
  const activeDate = pendingDate ? new Date(pendingDate) : dueDate ? current : null
  const selectedDay = activeDate
    ? `${activeDate.getFullYear()}-${pad(activeDate.getMonth() + 1)}-${pad(activeDate.getDate())}`
    : null

  const todayStr = (() => {
    const t = new Date()
    return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`
  })()

  // Build calendar grid
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1)
  // Monday=0 ... Sunday=6
  const startWeekday = (firstDayOfMonth.getDay() + 6) % 7
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate()

  const calendarDays: { day: number; month: number; year: number; current: boolean }[] = []
  // Previous month fill
  for (let i = startWeekday - 1; i >= 0; i--) {
    const pm = viewMonth === 0 ? 11 : viewMonth - 1
    const py = viewMonth === 0 ? viewYear - 1 : viewYear
    calendarDays.push({ day: daysInPrevMonth - i, month: pm, year: py, current: false })
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    calendarDays.push({ day: d, month: viewMonth, year: viewYear, current: true })
  }
  // Next month fill
  const remaining = 42 - calendarDays.length
  for (let d = 1; d <= remaining; d++) {
    const nm = viewMonth === 11 ? 0 : viewMonth + 1
    const ny = viewMonth === 11 ? viewYear + 1 : viewYear
    calendarDays.push({ day: d, month: nm, year: ny, current: false })
  }

  const handleDayClick = (day: { day: number; month: number; year: number }) => {
    const iso = new Date(day.year, day.month, day.day, selectedHour, selectedMinute).toISOString()
    setPendingDate(iso)
  }

  const handleTimeChange = (h: number, m: number) => {
    setSelectedHour(h)
    setSelectedMinute(m)
    // Update pending date with new time
    const base = pendingDate ? new Date(pendingDate) : dueDate ? new Date(dueDate) : null
    if (base) {
      base.setHours(h, m)
      setPendingDate(base.toISOString())
    }
  }

  const handleConfirm = () => {
    if (pendingDate) {
      onChange(pendingDate)
      setPendingDate(null)
    }
    setOpen(false)
  }

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1) }
    else setViewMonth(viewMonth - 1)
  }
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1) }
    else setViewMonth(viewMonth + 1)
  }

  const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']

  // Generate hour/minute options for scrollable lists
  const hours = Array.from({ length: 24 }, (_, i) => i)
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5)

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', color.bg, color.text)}
      >
        <Calendar className="w-3 h-3" />
        <span className="font-normal" style={{ color: 'inherit', opacity: 0.8 }}>{label}:</span>
        {dueDate ? formatDate(dueDate, { hour: '2-digit', minute: '2-digit' }) : 'Nicht gesetzt'}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-3 min-w-[350px]">
          <div className="flex gap-3">
            {/* Calendar */}
            <div className="w-[220px]">
              <div className="flex items-center justify-between mb-2">
                <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded text-gray-500"><ChevronDown className="w-4 h-4 rotate-90" /></button>
                <span className="text-sm font-medium text-gray-700">{monthNames[viewMonth]} {viewYear}</span>
                <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded text-gray-500"><ChevronDown className="w-4 h-4 -rotate-90" /></button>
              </div>
              <div className="grid grid-cols-7 text-center text-[10px] text-gray-400 mb-1">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => <span key={d}>{d}</span>)}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {calendarDays.map((d, i) => {
                  const dayStr = `${d.year}-${pad(d.month + 1)}-${pad(d.day)}`
                  const isSelected = dayStr === selectedDay
                  const isToday = dayStr === todayStr
                  return (
                    <button
                      key={i}
                      onClick={() => handleDayClick(d)}
                      className={cn(
                        'w-7 h-7 text-xs rounded-md flex items-center justify-center transition-colors',
                        !d.current && 'text-gray-300',
                        d.current && !isSelected && !isToday && 'text-gray-700 hover:bg-gray-100',
                        isToday && !isSelected && 'bg-gray-100 font-semibold text-gray-900',
                        isSelected && 'bg-brand-500 text-white font-semibold',
                      )}
                    >
                      {d.day}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Time selector */}
            <div className="border-l border-gray-200 pl-3 flex gap-2 shrink-0">
              <div className="flex flex-col items-center w-10">
                <span className="text-[10px] text-gray-400 mb-1">Std</span>
                <div className="h-[200px] overflow-y-auto overscroll-contain" style={{ scrollbarWidth: 'thin' }}>
                  {hours.map(h => (
                    <button
                      key={h}
                      onClick={() => handleTimeChange(h, selectedMinute)}
                      className={cn(
                        'block w-10 py-1 text-xs text-center rounded transition-colors',
                        h === selectedHour ? 'bg-brand-500 text-white font-semibold' : 'text-gray-600 hover:bg-gray-100',
                      )}
                    >
                      {pad(h)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-center w-10">
                <span className="text-[10px] text-gray-400 mb-1">Min</span>
                <div className="h-[200px] overflow-y-auto overscroll-contain" style={{ scrollbarWidth: 'thin' }}>
                  {minutes.map(m => (
                    <button
                      key={m}
                      onClick={() => handleTimeChange(selectedHour, m)}
                      className={cn(
                        'block w-10 py-1 text-xs text-center rounded transition-colors',
                        m === selectedMinute ? 'bg-brand-500 text-white font-semibold' : 'text-gray-600 hover:bg-gray-100',
                      )}
                    >
                      {pad(m)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Confirm / Remove buttons */}
          <div className="mt-2 border-t border-gray-100 pt-2 flex items-center gap-2">
            <Button
              variant="primary"
              size="xs"
              onClick={handleConfirm}
              disabled={!pendingDate}
              fullWidth
            >
              Übernehmen
            </Button>
            {dueDate && (
              <Button
                variant="linkDanger"
                size="xs"
                onClick={() => { onChange(null); setOpen(false) }}
              >
                Entfernen
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Generic TANSS Dropdown ─────────────────────────────────────────────────

function TanssDropdown({
  label, currentId, currentName, options, onChange, color, icon, nullable,
}: {
  label: string
  currentId: number | null | undefined
  currentName: string
  options: { id: number; name: string }[]
  onChange: (id: number | null) => void
  color: string
  icon?: React.ReactNode
  nullable?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setSearch('') }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const filtered = options.filter((o) =>
    !search.trim() || o.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', color)}>
        {icon}
        <span className="text-gray-400 font-normal">{label}:</span>
        {currentName}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[200px] max-h-[300px] flex flex-col">
          {options.length > 5 && (
            <div className="px-2 py-1.5 border-b border-gray-100">
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suchen..."
                autoFocus
                className="text-sm"
              />
            </div>
          )}
          <div className="overflow-y-auto max-h-[240px]">
            {nullable && (
              <button
                onClick={() => { onChange(null); setOpen(false); setSearch('') }}
                className={cn('w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 text-gray-400 italic', currentId == null && 'font-semibold')}
              >
                Nicht zugewiesen
              </button>
            )}
            {filtered.map((opt) => (
              <button
                key={opt.id}
                onClick={() => { onChange(opt.id); setOpen(false); setSearch('') }}
                className={cn('w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50', opt.id === currentId && 'font-semibold text-brand-700')}
              >
                {opt.name}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400">Keine Einträge gefunden</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Sticky Timer ───────────────────────────────────────────────────────────

interface TimerEntry {
  id: string
  timerId: number
  isRunning: boolean
  startTime: string
  duration: number
  description: string
  title: string
}

function TimerEntryRow({ entry, ticketId }: { entry: TimerEntry; ticketId: string }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [desc, setDesc] = useState(entry.description)
  const [elapsed, setElapsed] = useState(0)

  // Live elapsed counter for running timer
  useEffect(() => {
    if (!entry.isRunning) return
    const start = new Date(entry.startTime).getTime()
    const update = () => setElapsed(Math.floor((Date.now() - start) / 1000))
    update()
    const iv = setInterval(update, 1000)
    return () => clearInterval(iv)
  }, [entry.isRunning, entry.startTime])

  useEffect(() => { setDesc(entry.description) }, [entry.description])

  const saveMutation = useMutation({
    mutationFn: (description: string) =>
      apiClient.patch(`/tickets/${ticketId}/timer/${entry.timerId}`, { description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'timer'] })
      setEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/tickets/${ticketId}/timer/${entry.timerId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'timer'] }),
  })

  const formatDuration = (s: number) => {
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }

  const displayDuration = entry.isRunning ? elapsed : (entry.duration ?? 0)

  return (
    <div className={cn(
      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm',
      entry.isRunning ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200',
    )}>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {entry.isRunning ? (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
        ) : (
          <Clock className="w-3.5 h-3.5 text-gray-400" />
        )}
        <span className={cn(
          'font-mono font-bold tabular-nums',
          entry.isRunning ? 'text-green-600' : 'text-gray-600',
        )}>
          {formatDuration(displayDuration)}
        </span>
      </div>

      {editing ? (
        <div className="flex-1 flex items-center gap-2">
          <Input
            type="text"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                saveMutation.mutate(desc.trim())
              }
              if (e.key === 'Escape') setEditing(false)
            }}
            autoFocus
            placeholder="Beschreibung..."
            className="text-sm"
            wrapperClassName="flex-1"
          />
          <IconButton
            onClick={() => saveMutation.mutate(desc.trim())}
            disabled={saveMutation.isPending}
            icon={<Check className="w-3.5 h-3.5" />}
            aria-label="Speichern"
            variant="primary"
            size="xs"
          />
          <IconButton
            onClick={() => { setDesc(entry.description); setEditing(false) }}
            icon={<X className="w-3.5 h-3.5" />}
            aria-label="Abbrechen"
            variant="ghost"
            size="xs"
          />
        </div>
      ) : (
        <span
          className="flex-1 text-gray-700 truncate cursor-pointer hover:text-brand-600"
          onClick={() => setEditing(true)}
          title="Klicken zum Bearbeiten"
        >
          {entry.description || entry.title || <span className="text-gray-400 italic">Keine Beschreibung</span>}
        </span>
      )}

      {!editing && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <IconButton
            onClick={() => setEditing(true)}
            icon={<StickyNote className="w-3.5 h-3.5" />}
            aria-label="Bearbeiten"
            variant="ghost"
            size="xs"
          />
          {!entry.isRunning && (
            <IconButton
              onClick={() => { if (confirm('Timer löschen?')) deleteMutation.mutate() }}
              disabled={deleteMutation.isPending}
              icon={<X className="w-3.5 h-3.5" />}
              aria-label="Timer löschen"
              variant="danger"
              size="xs"
            />
          )}
        </div>
      )}
    </div>
  )
}

function StickyTimer({ ticketId }: { ticketId: string }) {
  const qc = useQueryClient()
  const [elapsed, setElapsed] = useState(0)
  const [description, setDescription] = useState('')
  const [expanded, setExpanded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: timer, isError: fetchError } = useQuery({
    queryKey: ['tickets', ticketId, 'timer'],
    queryFn: () => apiClient.get(`/tickets/${ticketId}/timer`).then((r) => r.data),
    refetchInterval: 30000,
  })

  const startMutation = useMutation({
    mutationFn: () => apiClient.post(`/tickets/${ticketId}/timer/start`),
    onSuccess: () => {
      setError(null)
      qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'timer'] })
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (err as Error)?.message || 'Timer konnte nicht gestartet werden'
      setError(msg)
      // Still refresh to get current state
      qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'timer'] })
    },
  })

  const stopMutation = useMutation({
    mutationFn: (desc?: string) => apiClient.post(`/tickets/${ticketId}/timer/stop`, { description: desc || undefined }),
    onSuccess: () => {
      setError(null)
      qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'timer'] })
      qc.invalidateQueries({ queryKey: ['tickets', ticketId] })
      setDescription('')
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (err as Error)?.message || 'Timer konnte nicht gestoppt werden'
      setError(msg)
      qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'timer'] })
    },
  })

  // Use isRunning from the backend response
  const isRunning = !!timer?.isRunning
  const activeEntry = timer?.entry
  const timerStartTime = activeEntry?.startTime

  useEffect(() => {
    if (!isRunning || !timerStartTime) { setElapsed(0); return }
    const start = new Date(timerStartTime).getTime()
    const update = () => setElapsed(Math.floor((Date.now() - start) / 1000))
    update()
    const iv = setInterval(update, 1000)
    return () => clearInterval(iv)
  }, [isRunning, timerStartTime])

  const formatElapsed = (s: number) => {
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (description.trim()) {
        const timerId = activeEntry?.timerId
        if (timerId) {
          apiClient.patch(`/tickets/${ticketId}/timer/${timerId}`, { description: description.trim() })
            .then(() => qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'timer'] }))
            .catch(() => setError('Notiz konnte nicht gespeichert werden'))
        } else {
          // Fallback: try the description endpoint
          apiClient.patch(`/tickets/${ticketId}/timer/description`, { description: description.trim() })
            .then(() => qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'timer'] }))
            .catch(() => setError('Notiz konnte nicht gespeichert werden'))
        }
      }
    }
  }

  const entries: TimerEntry[] = timer?.entries ?? []
  const hasEntries = entries.length > 0

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
      {/* Error display */}
      {(error || fetchError) && (
        <div className="max-w-screen-xl mx-auto px-4 pt-2">
          <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 px-3 py-1.5 rounded border border-red-200">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="flex-1">{error || 'Timer-Daten konnten nicht geladen werden'}</span>
            <IconButton onClick={() => setError(null)} icon={<X className="w-3 h-3" />} aria-label="Fehler schließen" variant="ghost" size="xs" className="text-red-400 hover:text-red-600" />
          </div>
        </div>
      )}

      {/* Timer entries list (expandable) */}
      {expanded && hasEntries && (
        <div className="max-w-screen-xl mx-auto px-4 pt-3 pb-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Timer ({entries.length})
            </span>
          </div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {entries.map((e) => (
              <TimerEntryRow key={e.id} entry={e} ticketId={ticketId} />
            ))}
          </div>
        </div>
      )}

      {/* Main control bar */}
      <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center gap-3">
        {/* Expand/collapse for timer list */}
        {hasEntries && (
          <IconButton
            onClick={() => setExpanded(!expanded)}
            icon={<ChevronDown className={cn('w-4 h-4 transition-transform', expanded && 'rotate-180')} />}
            aria-label={expanded ? 'Timer-Liste einklappen' : 'Timer-Liste ausklappen'}
            variant="ghost"
            size="sm"
            className="flex-shrink-0"
          />
        )}

        {isRunning ? (
          <>
            <span className="text-lg font-mono font-bold text-green-600 tabular-nums flex-shrink-0">
              {formatElapsed(elapsed)}
            </span>
            <Input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Dokumentation eingeben (Enter zum Speichern)..."
              className="text-sm"
              wrapperClassName="flex-1"
            />
            <Button
              variant="danger"
              size="md"
              onClick={() => stopMutation.mutate(description.trim() || undefined)}
              disabled={stopMutation.isPending}
              className="flex-shrink-0"
            >
              <Square className="w-3.5 h-3.5" />
              Stop
            </Button>
          </>
        ) : (
          <>
            <span className="text-sm text-gray-500 flex-1">
              {hasEntries
                ? `${entries.length} Timer · Kein aktiver Timer`
                : 'Kein Timer'}
            </span>
            <Button
              variant="primary"
              size="md"
              onClick={() => { setError(null); startMutation.mutate() }}
              disabled={startMutation.isPending}
              className="flex-shrink-0 bg-green-500 hover:bg-green-600"
            >
              <Play className="w-3.5 h-3.5" />
              Timer starten
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Activity Feed (unified list) ──────────────────────────────────────────

function ActivityFeed({ ticketId, ticket }: { ticketId: string; ticket: Record<string, unknown> }) {
  const qc = useQueryClient()
  const [commentTitle, setCommentTitle] = useState('')
  const [commentText, setCommentText] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [isPinned, setIsPinned] = useState(false)
  const [selectedItem, setSelectedItem] = useState<ActivityItem | null>(null)
  const [emailReplyDraft, setEmailReplyDraft] = useState('')

  // Vereinheitlichte History aus TANSS
  const { data: history } = useQuery({
    queryKey: ['tickets', ticketId, 'history'],
    queryFn: () => apiClient.get(`/tickets/${ticketId}/history`).then((r) => r.data),
  })

  const addComment = useMutation({
    mutationFn: () => apiClient.post(`/tickets/${ticketId}/comments`, {
      title: commentTitle || undefined,
      content: commentText,
      internal: isInternal,
      pinned: isPinned,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'history'] })
      qc.invalidateQueries({ queryKey: ['tickets', ticketId] })
      setCommentTitle('')
      setCommentText('')
      setIsPinned(false)
    },
  })

  const activities = useMemo<ActivityItem[]>(() => {
    const timeEntries = Array.isArray(ticket.timeEntries)
      ? (ticket.timeEntries as Record<string, unknown>[])
      : undefined
    return buildActivitiesFromTanssHistory(history as TanssHistoryRaw | undefined, { timeEntries })
  }, [history, ticket.timeEntries])

  // Listen for AI email draft events — opens the latest inbound email with the draft pre-filled
  useEffect(() => {
    const handler = (e: Event) => {
      const draft = (e as CustomEvent<string>).detail
      if (!draft) return
      const latestInbound = activities.find(
        (a) => a.kind === 'email' && (a.raw as { inbound?: unknown } | undefined)?.inbound !== false,
      )
      if (latestInbound) {
        setEmailReplyDraft(draft)
        setSelectedItem(latestInbound)
      }
    }
    window.addEventListener('ai-email-draft', handler)
    return () => window.removeEventListener('ai-email-draft', handler)
  }, [activities])

  return (
    <>
      <TicketActivityFeed
        items={activities}
        onItemClick={setSelectedItem}
        showInternalBadge
        footer={
          <ActivityCommentForm
            commentTitle={commentTitle}
            setCommentTitle={setCommentTitle}
            commentText={commentText}
            setCommentText={setCommentText}
            isInternal={isInternal}
            setIsInternal={setIsInternal}
            isPinned={isPinned}
            setIsPinned={setIsPinned}
            onSubmit={() => addComment.mutate()}
            submitting={addComment.isPending}
          />
        }
      />

      <InternalActivityModal
        item={selectedItem}
        onClose={() => { setSelectedItem(null); setEmailReplyDraft('') }}
        ticketId={ticketId}
        replyDraft={emailReplyDraft}
        onClearDraft={() => setEmailReplyDraft('')}
      />
    </>
  )
}

// ─── Comment Form (gerendert im Footer-Slot des TicketActivityFeed) ─────────

interface ActivityCommentFormProps {
  commentTitle: string
  setCommentTitle: (v: string) => void
  commentText: string
  setCommentText: (v: string) => void
  isInternal: boolean
  setIsInternal: (v: boolean) => void
  isPinned: boolean
  setIsPinned: (v: boolean) => void
  onSubmit: () => void
  submitting: boolean
}

function ActivityCommentForm({
  commentTitle, setCommentTitle, commentText, setCommentText,
  isInternal, setIsInternal, isPinned, setIsPinned,
  onSubmit, submitting,
}: ActivityCommentFormProps) {
  return (
    <div className="p-4 space-y-2">
      <Input
        value={commentTitle}
        onChange={(e) => setCommentTitle(e.target.value)}
        placeholder="Betreff (optional)"
      />
      <Textarea
        value={commentText}
        onChange={(e) => setCommentText(e.target.value)}
        placeholder="Kommentar schreiben..."
        rows={3}
      />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-4">
          <Checkbox
            checked={isInternal}
            onChange={(e) => setIsInternal(e.target.checked)}
            label={
              <span className="inline-flex items-center gap-1">
                <Lock className="w-3 h-3" />
                intern setzen
              </span>
            }
          />
          <Checkbox
            checked={isPinned}
            onChange={(e) => setIsPinned(e.target.checked)}
            label={
              <span className="inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Als wichtig markieren
              </span>
            }
          />
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={onSubmit}
          loading={submitting}
          disabled={!commentText.trim()}
        >
          <Send className="w-3 h-3" />
          Kommentar speichern
        </Button>
      </div>
    </div>
  )
}

// ─── Internal Activity Modal — wraps shared ActivityDetailModal with edit/reply

interface InternalActivityModalProps {
  item: ActivityItem | null
  onClose: () => void
  ticketId: string
  replyDraft?: string
  onClearDraft?: () => void
}

function InternalActivityModal({
  item, onClose, ticketId, replyDraft, onClearDraft,
}: InternalActivityModalProps) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [replying, setReplying] = useState(false)
  const [replyText, setReplyText] = useState('')

  // Support edit state — initialisiert sich aus dem aktuell gewaehlten Item.
  const supportRaw = item?.raw as Record<string, unknown> | undefined
  const [editText, setEditText] = useState('')
  const [editTextIntern, setEditTextIntern] = useState('')
  const [editDuration, setEditDuration] = useState(0)
  const [editLocation, setEditLocation] = useState('OFFICE')
  const [editDurationNotCharged, setEditDurationNotCharged] = useState(0)

  // Reset state wenn ein anderes Item geoeffnet wird, oder ein Reply-Draft
  // (z. B. KI-generiert) reinkommt.
  useEffect(() => {
    if (!item) {
      setEditing(false)
      setReplying(false)
      setReplyText('')
      return
    }
    setEditText(item.content)
    setEditTextIntern(typeof supportRaw?.textIntern === 'string' ? supportRaw.textIntern : '')
    setEditDuration(typeof supportRaw?.duration === 'number' ? supportRaw.duration : 0)
    setEditLocation(typeof supportRaw?.location === 'string' ? supportRaw.location : 'OFFICE')
    setEditDurationNotCharged(typeof supportRaw?.durationNotCharged === 'number' ? supportRaw.durationNotCharged : 0)
    setEditing(false)
    if (replyDraft) {
      setReplyText(replyDraft)
      setReplying(true)
    } else {
      setReplyText('')
      setReplying(false)
    }
  }, [item, replyDraft, supportRaw])

  const tanssId = typeof supportRaw?.id === 'number' ? supportRaw.id : null
  const updateSupport = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient.put(`/tickets/${ticketId}/supports/${tanssId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'history'] })
      onClose()
    },
  })

  const sendReply = useMutation({
    mutationFn: () =>
      apiClient.post(`/tickets/${ticketId}/comments`, {
        title: `Re: ${(supportRaw as { subject?: string } | undefined)?.subject || item?.title || ''}`,
        content: replyText,
        internal: false,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'history'] })
      setReplying(false)
      setReplyText('')
      onClearDraft?.()
    },
  })

  const canEdit = item?.kind === 'support' && tanssId != null
  const locationLabels: Record<string, string> = { OFFICE: 'Büro', CUSTOMER: 'Kunde', REMOTE: 'Remote' }
  const senderEmail = typeof (item?.raw as { senderEmail?: string } | undefined)?.senderEmail === 'string'
    ? (item!.raw as { senderEmail?: string }).senderEmail
    : undefined

  const headerActions = item ? (
    <>
      {item.kind === 'email' && !replying ? (
        <Button variant="link" size="xs" onClick={() => setReplying(true)}>
          <Mail className="w-3 h-3" />
          Antworten
        </Button>
      ) : null}
      {canEdit && !editing ? (
        <Button variant="link" size="xs" onClick={() => setEditing(true)}>
          Bearbeiten
        </Button>
      ) : null}
    </>
  ) : null

  const editForm = (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <Input
          type="number"
          label="Dauer (Min.)"
          value={editDuration}
          onChange={(e) => setEditDuration(Number(e.target.value))}
        />
        <Input
          type="number"
          label="Nicht berechnen (Min.)"
          value={editDurationNotCharged}
          onChange={(e) => setEditDurationNotCharged(Number(e.target.value))}
        />
        <Select
          label="Ort"
          value={editLocation}
          onValueChange={setEditLocation}
          options={Object.entries(locationLabels).map(([value, label]) => ({ value, label }))}
        />
      </div>
      <Textarea label="Text" value={editText} onChange={(e) => setEditText(e.target.value)} rows={4} />
      <Textarea
        label="Interne Bemerkung"
        value={editTextIntern}
        onChange={(e) => setEditTextIntern(e.target.value)}
        rows={2}
        className="bg-amber-50"
      />
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Abbrechen</Button>
        <Button
          variant="primary"
          size="sm"
          loading={updateSupport.isPending}
          onClick={() => updateSupport.mutate({
            text: editText,
            textIntern: editTextIntern,
            duration: editDuration,
            location: editLocation,
            durationNotCharged: editDurationNotCharged,
          })}
        >
          Speichern
        </Button>
      </div>
    </div>
  )

  const replyForm = (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-4 h-4 text-brand-600" />
        <span className="text-small font-semibold text-gray-700">Antwort</span>
        {senderEmail ? (
          <span className="text-caption text-gray-500">an {senderEmail}</span>
        ) : null}
      </div>
      <Textarea
        value={replyText}
        onChange={(e) => setReplyText(e.target.value)}
        rows={6}
        placeholder="Antwort verfassen..."
      />
      <div className="flex justify-end gap-2 mt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setReplying(false); setReplyText(''); onClearDraft?.() }}
        >
          Abbrechen
        </Button>
        <Button
          variant="primary"
          size="sm"
          loading={sendReply.isPending}
          disabled={!replyText.trim()}
          onClick={() => sendReply.mutate()}
        >
          <Send className="w-3 h-3" />
          Kommentar speichern
        </Button>
      </div>
    </div>
  )

  return (
    <ActivityDetailModal
      item={item}
      onClose={onClose}
      showInternalBadge
      headerActions={headerActions}
      bodyOverride={editing ? editForm : undefined}
      renderContent={(content) => <>{linkifyPhones(content)}</>}
    >
      {replying && item?.kind === 'email' ? replyForm : null}
    </ActivityDetailModal>
  )
}


// ─── Password Generator Tab ────────────────────────────────────────────────

function PasswordGeneratorTab() {
  const [password, setPassword] = useState(() => generatePassword(16))
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(password)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="p-5 space-y-3">
      <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3 font-mono text-sm">
        <span className="flex-1 text-gray-800 break-all">{password}</span>
        <IconButton onClick={copy} icon={copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />} aria-label="Passwort kopieren" variant="ghost" size="sm" className="flex-shrink-0" />
      </div>
      <div className="flex gap-2">
        {[12, 16, 20, 32].map((len) => (
          <Button
            key={len}
            variant="secondary"
            size="xs"
            onClick={() => setPassword(generatePassword(len))}
          >
            {len} Zeichen
          </Button>
        ))}
        <Button
          variant="primary"
          size="xs"
          onClick={() => setPassword(generatePassword(16))}
          className="ml-auto"
        >
          <RefreshCw className="w-3 h-3" />
          Neu
        </Button>
      </div>
    </div>
  )
}

// ─── Password Send Tab ─────────────────────────────────────────────────────

function PasswordSendTab() {
  const [form, setForm] = useState({ password: '', deliveryMethod: 'sms' as 'sms' | 'email', recipient: '', expiresInHours: 24 })
  const [result, setResult] = useState<{ shareUrl: string; expiresAt: string } | null>(null)
  const [copied, setCopied] = useState(false)

  const send = useMutation({
    mutationFn: () => apiClient.post('/passwords/share', form).then((r) => r.data),
    onSuccess: (data) => setResult(data),
  })

  const copy = async () => {
    if (!result) return
    await navigator.clipboard.writeText(result.shareUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (result) {
    return (
      <div className="p-5 space-y-3">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700 font-semibold text-sm">Passwort versendet!</p>
          <div className="flex items-center gap-2 bg-white rounded-lg p-2 mt-2 border border-green-200">
            <span className="flex-1 text-xs font-mono text-gray-700 break-all">{result.shareUrl}</span>
            <IconButton onClick={copy} icon={copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 text-gray-500" />} aria-label="Link kopieren" variant="ghost" size="xs" />
          </div>
          <Button
            variant="link"
            size="xs"
            onClick={() => { setResult(null); setForm({ password: '', deliveryMethod: 'sms', recipient: '', expiresInHours: 24 }) }}
            className="mt-2 text-green-700"
          >
            Neues Passwort versenden
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-5 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Input
            type="password"
            label="Passwort"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="Passwort eingeben"
          />
        </div>
        <div className="col-span-2">
          <Input
            type="text"
            label="Empfänger"
            value={form.recipient}
            onChange={(e) => setForm((f) => ({ ...f, recipient: e.target.value }))}
            placeholder={form.deliveryMethod === 'sms' ? '+49 171 ...' : 'email@firma.de'}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Methode</label>
          <div className="flex gap-1">
            {(['sms', 'email'] as const).map((m) => (
              <Button
                key={m}
                variant={form.deliveryMethod === m ? 'primary' : 'secondary'}
                size="xs"
                fullWidth
                onClick={() => setForm((f) => ({ ...f, deliveryMethod: m }))}
              >
                {m === 'sms' ? 'SMS' : 'E-Mail'}
              </Button>
            ))}
          </div>
        </div>
        <Select
          label="Gültig für"
          value={String(form.expiresInHours)}
          onValueChange={(v) => setForm((f) => ({ ...f, expiresInHours: Number(v) }))}
          options={[
            { value: '1', label: '1 Stunde' },
            { value: '6', label: '6 Stunden' },
            { value: '24', label: '24 Stunden' },
            { value: '48', label: '2 Tage' },
          ]}
        />
      </div>
      <Button
        variant="primary"
        fullWidth
        onClick={() => send.mutate()}
        disabled={!form.password || !form.recipient}
        loading={send.isPending}
      >
        <Send className="w-3.5 h-3.5" />
        Passwort versenden
      </Button>
    </div>
  )
}

// ─── E-Mails Tab (FA-15) ──────────────────────────────────────────────────────

interface LinkedEmail {
  id: number
  email_id: string
  direction: 'inbound' | 'outbound'
  matched_by: 'manual' | 'thread' | 'subject'
  created_at: string
  subject: string | null
  from: string | null
  fromName: string | null
  receivedDateTime: string | null
  isRead: boolean | null
}

function TicketEmailsTab({ ticketId }: { ticketId: string }) {
  const { data: emails = [], isLoading, error } = useQuery<LinkedEmail[]>({
    queryKey: ['ticket-emails', ticketId],
    queryFn: () => apiClient.get(`/tickets/${ticketId}/emails`).then((r) => r.data),
  })

  if (isLoading) return <div className="p-6 text-center text-sm text-gray-500">Lade E-Mails…</div>
  if (error) return <div className="p-6 text-center text-sm text-red-600">E-Mails konnten nicht geladen werden.</div>
  if (emails.length === 0) return (
    <div className="p-6 text-center text-sm text-gray-500">
      Keine verknüpften E-Mails. E-Mails werden automatisch zugeordnet oder können manuell im Posteingang verknüpft werden.
    </div>
  )

  return (
    <div className="divide-y divide-gray-100">
      {emails.map((email) => (
        <div key={email.id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50">
          <Mail className={`w-4 h-4 mt-0.5 shrink-0 ${email.direction === 'inbound' ? 'text-blue-500' : 'text-green-500'}`} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${email.direction === 'inbound' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                {email.direction === 'inbound' ? 'Eingang' : 'Ausgang'}
              </span>
              <span className="text-xs text-gray-400">{email.matched_by === 'manual' ? 'manuell' : email.matched_by === 'thread' ? 'Thread' : 'Betreff'}</span>
              {email.receivedDateTime && (
                <span className="text-xs text-gray-400 ml-auto">{formatDate(email.receivedDateTime)}</span>
              )}
            </div>
            <p className="text-sm font-medium text-gray-800 mt-0.5 truncate">{email.subject ?? '(Kein Betreff)'}</p>
            {email.fromName || email.from ? (
              <p className="text-xs text-gray-500 truncate">{email.fromName ?? email.from}</p>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── E-Mail schreiben Tab (FA-13) ─────────────────────────────────────────────

function TicketEmailComposeTab({ ticketId, ticket, onSent }: { ticketId: string; ticket: Record<string, unknown>; onSent: () => void }) {
  const { toast } = useToast()
  const qc = useQueryClient()

  // Pre-fill subject with ticket reference pattern
  const tanssId = (ticket?.tanssId ?? ticket?.id ?? ticketId) as string | number
  const ticketTitle = (ticket?.title ?? '') as string
  const defaultSubject = `[Ticket #${tanssId}] ${ticketTitle}`.trim()

  // Try to get contact email from ticket
  const contactEmail = (ticket?.contactEmail ?? ticket?.customerEmail ?? '') as string

  const [to, setTo] = useState(contactEmail)
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState(defaultSubject)
  const [body, setBody] = useState('')
  const [showCc, setShowCc] = useState(false)

  const sendMutation = useMutation({
    mutationFn: async () => {
      const toArr = to.split(',').map((s) => s.trim()).filter(Boolean)
      const ccArr = cc.split(',').map((s) => s.trim()).filter(Boolean)
      const html = `<div>${body.replace(/\n/g, '<br/>')}</div>`
      await apiClient.post('/email/send', {
        to: toArr,
        subject,
        html,
        cc: ccArr.length ? ccArr : undefined,
      })
      // Auto-match will pick up the outbound link via [Ticket #XXXX] subject pattern within 10 min
    },
    onSuccess: () => {
      toast({ title: 'E-Mail gesendet', tone: 'success' })
      qc.invalidateQueries({ queryKey: ['ticket-emails', ticketId] })
      onSent()
    },
    onError: (err: Error) => toast({ title: 'Fehler beim Senden', description: err.message, tone: 'danger' }),
  })

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium w-14 shrink-0">An</label>
        <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="empfaenger@beispiel.de" className="flex-1" />
        <button type="button" className="text-xs text-brand-600 hover:underline shrink-0" onClick={() => setShowCc((v) => !v)}>CC</button>
      </div>
      {showCc && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium w-14 shrink-0">CC</label>
          <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="cc@beispiel.de" className="flex-1" />
        </div>
      )}
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium w-14 shrink-0">Betreff</label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Betreff" className="flex-1" />
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Ihre Nachricht…"
        className="w-full min-h-[200px] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y"
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onSent}>Abbrechen</Button>
        <Button
          variant="primary"
          onClick={() => sendMutation.mutate()}
          disabled={sendMutation.isPending || !to || !subject}
        >
          <Send className="w-3.5 h-3.5 mr-1.5" />
          {sendMutation.isPending ? 'Wird gesendet…' : 'Senden'}
        </Button>
      </div>
    </div>
  )
}

// ─── WhatsApp Chat-Verlauf Tab (CODA-1754) ───────────────────────────────────
// API contract: GET /api/tickets/:id/whatsapp
// → { conversation: WhatsAppChatConversation | null, messages: WhatsAppChatMessage[] }
// POST /api/tickets/:id/whatsapp/reply { body: string }

interface WhatsAppChatConversation {
  id: string
  phoneFrom: string
  customerName: string | null   // from TANSS contact match
  status: 'active' | 'closed'
}

interface WhatsAppChatMessage {
  id: string
  direction: 'inbound' | 'outbound'
  body: string | null
  mediaUrls?: string[]
  sentAt: string
}

interface WhatsAppTabData {
  conversation: WhatsAppChatConversation | null
  messages: WhatsAppChatMessage[]
}

function formatWhatsAppTime(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const isToday = d.toDateString() === now.toDateString()
  if (isToday) {
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  }
  return (
    d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
    ' ' +
    d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  )
}

function TicketWhatsAppTab({ ticketId, ticket: _ticket }: { ticketId: string; ticket: Record<string, unknown> }) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [replyBody, setReplyBody] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, error } = useQuery<WhatsAppTabData>({
    queryKey: ['ticket-whatsapp', ticketId],
    queryFn: () => apiClient.get(`/tickets/${ticketId}/whatsapp`).then((r) => r.data),
    refetchInterval: 30_000,
  })

  useEffect(() => {
    if (data?.messages?.length) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [data?.messages?.length])

  const replyMutation = useMutation({
    mutationFn: async (body: string) => {
      await apiClient.post(`/tickets/${ticketId}/whatsapp/reply`, { body })
    },
    onSuccess: () => {
      toast({ title: 'WhatsApp-Nachricht gesendet', tone: 'success' })
      setReplyBody('')
      qc.invalidateQueries({ queryKey: ['ticket-whatsapp', ticketId] })
    },
    onError: (err: Error) => toast({ title: 'Fehler beim Senden', description: err.message, tone: 'danger' }),
  })

  if (isLoading) return <LoadingState label="Lade WhatsApp-Verlauf…" />

  if (error) {
    return (
      <ErrorState
        title="WhatsApp-Verlauf nicht verfügbar"
        description="Konnte den Chat-Verlauf nicht laden."
        details={(error as Error).message}
      />
    )
  }

  if (!data?.conversation) {
    return (
      <EmptyState
        icon={<MessageCircle className="w-10 h-10 text-gray-300" />}
        title="Kein WhatsApp-Gespräch verknüpft"
        description="Sobald eine WhatsApp-Nachricht von diesem Kunden eingeht und ein Ticket erstellt wird, erscheint der Chatverlauf hier automatisch."
      />
    )
  }

  const { conversation, messages } = data
  const displayName = conversation.customerName ?? conversation.phoneFrom

  return (
    <div className="flex flex-col h-[520px]">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
        <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center shrink-0">
          <MessageCircle className="w-5 h-5 text-green-600" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{displayName}</p>
          {conversation.customerName && (
            <p className="text-xs text-gray-500 truncate">{conversation.phoneFrom}</p>
          )}
        </div>
        <span className="ml-auto text-xs text-gray-400 shrink-0">{messages.length} Nachrichten</span>
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-[#efeae2]">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm text-gray-500">
            Noch keine Nachrichten in diesem Gespräch.
          </div>
        ) : (
          messages.map((msg) => {
            const isOutbound = msg.direction === 'outbound'
            return (
              <div
                key={msg.id}
                className={cn('flex', isOutbound ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[75%] rounded-lg px-3 py-2 shadow-sm',
                    isOutbound ? 'bg-[#dcf8c6] rounded-tr-none' : 'bg-white rounded-tl-none',
                  )}
                >
                  {!isOutbound && conversation.customerName && (
                    <p className="text-xs font-semibold text-green-700 mb-0.5">{conversation.customerName}</p>
                  )}
                  {msg.body ? (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{msg.body}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">[Medien-Nachricht]</p>
                  )}
                  {msg.mediaUrls && msg.mediaUrls.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {msg.mediaUrls.map((url, i) => (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 underline"
                        >
                          Anhang {i + 1}
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-end mt-1">
                    <span className="text-[10px] text-gray-400">{formatWhatsAppTime(msg.sentAt)}</span>
                  </div>
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Reply composer */}
      <div className="flex items-end gap-2 px-4 py-3 border-t border-gray-100 bg-white rounded-b-lg">
        <Textarea
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          placeholder="Antwort schreiben… (Enter = senden, Shift+Enter = neue Zeile)"
          rows={2}
          className="flex-1 resize-none"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              if (replyBody.trim()) replyMutation.mutate(replyBody.trim())
            }
          }}
        />
        <Button
          variant="primary"
          size="sm"
          onClick={() => replyBody.trim() && replyMutation.mutate(replyBody.trim())}
          disabled={replyMutation.isPending || !replyBody.trim()}
        >
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  )
}
